/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
const Ue = Symbol("Comlink.proxy"), st = Symbol("Comlink.endpoint"), lt = Symbol("Comlink.releaseProxy"), te = Symbol("Comlink.finalizer"), Z = Symbol("Comlink.thrown"), $e = (e) => typeof e == "object" && e !== null || typeof e == "function", ct = {
  canHandle: (e) => $e(e) && e[Ue],
  serialize(e) {
    const { port1: t, port2: a } = new MessageChannel();
    return De(e, t), [a, [a]];
  },
  deserialize(e) {
    return e.start(), We(e);
  }
}, dt = {
  canHandle: (e) => $e(e) && Z in e,
  serialize({ value: e }) {
    let t;
    return e instanceof Error ? t = {
      isError: !0,
      value: {
        message: e.message,
        name: e.name,
        stack: e.stack
      }
    } : t = { isError: !1, value: e }, [t, []];
  },
  deserialize(e) {
    throw e.isError ? Object.assign(new Error(e.value.message), e.value) : e.value;
  }
}, $ = /* @__PURE__ */ new Map([
  ["proxy", ct],
  ["throw", dt]
]);
function mt(e, t) {
  for (const a of e)
    if (t === a || a === "*" || a instanceof RegExp && a.test(t))
      return !0;
  return !1;
}
function De(e, t = globalThis, a = ["*"]) {
  t.addEventListener("message", function i(n) {
    if (!n || !n.data)
      return;
    if (!mt(a, n.origin)) {
      console.warn(`Invalid origin '${n.origin}' for comlink proxy`);
      return;
    }
    const { id: o, type: p, path: r } = Object.assign({ path: [] }, n.data), l = (n.data.argumentList || []).map(T);
    let s;
    try {
      const c = r.slice(0, -1).reduce((f, u) => f[u], e), m = r.reduce((f, u) => f[u], e);
      switch (p) {
        case "GET":
          s = m;
          break;
        case "SET":
          c[r.slice(-1)[0]] = T(n.data.value), s = !0;
          break;
        case "APPLY":
          s = m.apply(c, l);
          break;
        case "CONSTRUCT":
          {
            const f = new m(...l);
            s = Ne(f);
          }
          break;
        case "ENDPOINT":
          {
            const { port1: f, port2: u } = new MessageChannel();
            De(e, u), s = U(f, [f]);
          }
          break;
        case "RELEASE":
          s = void 0;
          break;
        default:
          return;
      }
    } catch (c) {
      s = { value: c, [Z]: 0 };
    }
    Promise.resolve(s).catch((c) => ({ value: c, [Z]: 0 })).then((c) => {
      const [m, f] = Q(c);
      t.postMessage(Object.assign(Object.assign({}, m), { id: o }), f), p === "RELEASE" && (t.removeEventListener("message", i), qe(t), te in e && typeof e[te] == "function" && e[te]());
    }).catch((c) => {
      const [m, f] = Q({
        value: new TypeError("Unserializable return value"),
        [Z]: 0
      });
      t.postMessage(Object.assign(Object.assign({}, m), { id: o }), f);
    });
  }), t.start && t.start();
}
function ft(e) {
  return e.constructor.name === "MessagePort";
}
function qe(e) {
  ft(e) && e.close();
}
function We(e, t) {
  const a = /* @__PURE__ */ new Map();
  return e.addEventListener("message", function(n) {
    const { data: o } = n;
    if (!o || !o.id)
      return;
    const p = a.get(o.id);
    if (p)
      try {
        p(o);
      } finally {
        a.delete(o.id);
      }
  }), re(e, a, [], t);
}
function H(e) {
  if (e)
    throw new Error("Proxy has been released and is not useable");
}
function Oe(e) {
  return L(e, /* @__PURE__ */ new Map(), {
    type: "RELEASE"
  }).then(() => {
    qe(e);
  });
}
const V = /* @__PURE__ */ new WeakMap(), J = "FinalizationRegistry" in globalThis && new FinalizationRegistry((e) => {
  const t = (V.get(e) || 0) - 1;
  V.set(e, t), t === 0 && Oe(e);
});
function ut(e, t) {
  const a = (V.get(t) || 0) + 1;
  V.set(t, a), J && J.register(e, t, e);
}
function vt(e) {
  J && J.unregister(e);
}
function re(e, t, a = [], i = function() {
}) {
  let n = !1;
  const o = new Proxy(i, {
    get(p, r) {
      if (H(n), r === lt)
        return () => {
          vt(o), Oe(e), t.clear(), n = !0;
        };
      if (r === "then") {
        if (a.length === 0)
          return { then: () => o };
        const l = L(e, t, {
          type: "GET",
          path: a.map((s) => s.toString())
        }).then(T);
        return l.then.bind(l);
      }
      return re(e, t, [...a, r]);
    },
    set(p, r, l) {
      H(n);
      const [s, c] = Q(l);
      return L(e, t, {
        type: "SET",
        path: [...a, r].map((m) => m.toString()),
        value: s
      }, c).then(T);
    },
    apply(p, r, l) {
      H(n);
      const s = a[a.length - 1];
      if (s === st)
        return L(e, t, {
          type: "ENDPOINT"
        }).then(T);
      if (s === "bind")
        return re(e, t, a.slice(0, -1));
      const [c, m] = ye(l);
      return L(e, t, {
        type: "APPLY",
        path: a.map((f) => f.toString()),
        argumentList: c
      }, m).then(T);
    },
    construct(p, r) {
      H(n);
      const [l, s] = ye(r);
      return L(e, t, {
        type: "CONSTRUCT",
        path: a.map((c) => c.toString()),
        argumentList: l
      }, s).then(T);
    }
  });
  return ut(o, e), o;
}
function gt(e) {
  return Array.prototype.concat.apply([], e);
}
function ye(e) {
  const t = e.map(Q);
  return [t.map((a) => a[0]), gt(t.map((a) => a[1]))];
}
const Be = /* @__PURE__ */ new WeakMap();
function U(e, t) {
  return Be.set(e, t), e;
}
function Ne(e) {
  return Object.assign(e, { [Ue]: !0 });
}
function Q(e) {
  for (const [t, a] of $)
    if (a.canHandle(e)) {
      const [i, n] = a.serialize(e);
      return [
        {
          type: "HANDLER",
          name: t,
          value: i
        },
        n
      ];
    }
  return [
    {
      type: "RAW",
      value: e
    },
    Be.get(e) || []
  ];
}
function T(e) {
  switch (e.type) {
    case "HANDLER":
      return $.get(e.name).deserialize(e.value);
    case "RAW":
      return e.value;
  }
}
function L(e, t, a, i) {
  return new Promise((n) => {
    const o = xt();
    t.set(o, n), e.start && e.start(), e.postMessage(Object.assign({ id: o }, a), i);
  });
}
function xt() {
  return new Array(4).fill(0).map(() => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(16)).join("-");
}
const S = CustomEvent;
let ht = null;
function K() {
  return ht;
}
$.set("penumbra-progress", {
  /**
   * Checks if object is a penumbra-progress event
   * @param object - Object being passed through Comlink.transfer()
   * @returns true if the object is a penumbra-progress PenumbraEvent
   */
  canHandle(e) {
    return e instanceof S && e.type === "penumbra-progress";
  },
  /**
   * Serialize penumbra-progress event down to just ProgressDetails
   * @param object - Reference to the penumbra-progress PenumbraEvent
   * @returns [Clonables (structured-clone-compatible objects), [Transferables]]
   */
  serialize(e) {
    return [{ ...e.detail, worker: K() }, []];
  },
  /**
   * Re-create PenumbraEvent for re-dispatch in current context
   * @param detail - Structured-clone data from serialize()
   * @returns A re-created penumbra-progress PenumbraEvent
   */
  deserialize(e) {
    return new S("penumbra-progress", { detail: e });
  }
});
$.set("penumbra-complete", {
  /**
   * Checks if object is a penumbra-complete event
   * @param object - Object being passed through Comlink.transfer()
   * @returns true if the object is a penumbra-progress PenumbraEvent
   */
  canHandle(e) {
    return e instanceof S && e.type === "penumbra-complete";
  },
  /**
   * Serialize penumbra-complete event down to just JobCompletion
   * @param object - Reference to the penumbra-complete PenumbraEvent
   * @returns [Clonables (structured-clone-compatible objects), [Transferables]]
   */
  serialize(e) {
    return [{ ...e.detail, worker: K() }, []];
  },
  /**
   * Re-create PenumbraEvent for re-dispatch in current context
   * @param detail - Structured-clone data from serialize()
   * @returns A re-created penumbra-complete PenumbraEvent
   */
  deserialize(e) {
    return new S("penumbra-complete", {
      detail: e
    });
  }
});
$.set("penumbra-error", {
  /**
   * Checks if object is a penumbra-error event
   * @param object - Object being passed through Comlink.transfer()
   * @returns true if the object is a penumbra-progress PenumbraEvent
   */
  canHandle(e) {
    return e instanceof S && e.type === "penumbra-error";
  },
  /**
   * Serialize penumbra-progress event down to just ProgressDetails
   * @param object - Reference to the penumbra-progress PenumbraEvent
   * @returns [Clonables (structured-clone-compatible objects), [Transferables]]
   */
  serialize(e) {
    return [{ ...e.detail, worker: K() }, []];
  },
  /**
   * Re-create PenumbraEvent for re-dispatch in current context
   * @param detail - Structured-clone data from serialize()
   * @returns A re-created penumbra-progress PenumbraEvent
   */
  deserialize(e) {
    return new S("penumbra-error", { detail: e });
  }
});
$.set("error", {
  /**
   * Checks if object is an error event
   * @param object - Object being passed through Comlink.transfer()
   * @returns true if the object is a penumbra-progress PenumbraEvent
   */
  canHandle(e) {
    return e instanceof ErrorEvent && e.type === "error";
  },
  /**
   * Serialize penumbra-progress event down to just ProgressDetails
   * @param object - Reference to the penumbra-progress PenumbraEvent
   * @returns [Clonables (structured-clone-compatible objects), [Transferables]]
   */
  serialize(e) {
    return [{ ...e, worker: K() }, []];
  },
  /**
   * Re-create PenumbraEvent for re-dispatch in current context
   * @param detail - Structured-clone data from serialize()
   * @returns A re-created penumbra-progress PenumbraEvent
   */
  deserialize(e) {
    const t = new ErrorEvent("error", e);
    if (e)
      for (const a of Object.keys(e)) {
        const i = Object.getOwnPropertyDescriptor(t, a);
        if (a !== "isTrusted" && i && (i.configurable || "set" in i)) {
          const n = Object.getOwnPropertyDescriptor(e, a);
          n && Object.defineProperty(t, a, n);
        }
      }
    return t;
  }
});
function D(e) {
  let t = e.length;
  for (; --t >= 0; )
    e[t] = 0;
}
const bt = 3, wt = 258, Fe = 29, yt = 256, kt = yt + 1 + Fe, He = 30, Et = 512, jt = new Array((kt + 2) * 2);
D(jt);
const zt = new Array(He * 2);
D(zt);
const St = new Array(Et);
D(St);
const _t = new Array(wt - bt + 1);
D(_t);
const Tt = new Array(Fe);
D(Tt);
const Pt = new Array(He);
D(Pt);
try {
  String.fromCharCode.apply(null, new Uint8Array(1));
} catch {
}
const se = new Uint8Array(256);
for (let e = 0; e < 256; e++)
  se[e] = e >= 252 ? 6 : e >= 248 ? 5 : e >= 240 ? 4 : e >= 224 ? 3 : e >= 192 ? 2 : 1;
se[254] = se[254] = 1;
const Mt = {
  // constructor
  BigInt,
  // note: JSBI toString is already the same: a.toString()
  toNumber: Number,
  // binary functions to expressions
  add: (e, t) => e + t,
  subtract: (e, t) => e - t,
  multiply: (e, t) => e * t,
  divide: (e, t) => e / t,
  remainder: (e, t) => e % t,
  exponentiate: (e, t) => e ** t,
  leftShift: (e, t) => e << t,
  signedRightShift: (e, t) => e >> t,
  bitwiseAnd: (e, t) => e & t,
  bitwiseOr: (e, t) => e | t,
  bitwiseXor: (e, t) => e ^ t,
  equal: (e, t) => e === t,
  notEqual: (e, t) => e !== t,
  lessThan: (e, t) => e < t,
  lessThanOrEqual: (e, t) => e <= t,
  greaterThan: (e, t) => e > t,
  greaterThanOrEqual: (e, t) => e >= t,
  EQ: (e, t) => e == t,
  NE: (e, t) => e != t,
  LT: (e, t) => e < t,
  LE: (e, t) => e <= t,
  GT: (e, t) => e > t,
  GE: (e, t) => e >= t,
  ADD: (e, t) => e + t,
  // unary functions to expressions
  unaryMinus: (e) => -e,
  bitwiseNot: (e) => ~e,
  // static methods
  asIntN: (e, t) => BigInt.asIntN(e, t),
  asUintN: (e, t) => BigInt.asUintN(e, t)
};
if (!self.BigInt && !self.JSBI)
  throw new Error("BigInt is not supported in this browser. Conflux is unable to create zip files without BigInt support, or a JSBI polyfill.");
const g = !self.BigInt && self.JSBI ? self.JSBI : Mt;
function Lt() {
  const e = [];
  for (let t = 0; t < 256; t++) {
    let a = t;
    for (let i = 0; i < 8; i++)
      a = a & 1 ? a >>> 1 ^ 3988292384 : a >>> 1;
    e[t] = a;
  }
  return e;
}
class ge {
  crc;
  static table = Lt();
  constructor() {
    this.crc = -1;
  }
  append(t) {
    let a = Math.trunc(this.crc);
    const { table: i } = ge;
    for (let n = 0, o = Math.trunc(t.length); n < o; n++) {
      const p = t[n];
      if (p === void 0)
        continue;
      const r = i[(a ^ p) & 255];
      r !== void 0 && (a = a >>> 8 ^ r);
    }
    this.crc = a;
  }
  get() {
    return (this.crc ^ -1) >>> 0;
  }
}
/**
 * Conflux
 * Read (and build) zip files with whatwg streams in the browser.
 *
 * @author Transcend Inc. <https://transcend.io>
 * @license MIT
 */
new TextDecoder();
/**
 * Conflux
 * Build (and read) zip files with whatwg streams in the browser.
 *
 * @author Transcend Inc. <https://transcend.io>
 * @license MIT
 */
const ke = new TextEncoder();
class It {
  files;
  offset;
  constructor() {
    this.files = /* @__PURE__ */ Object.create(null), this.offset = g.BigInt(0);
  }
  /**
   * Transforms a stream of files into one zipped file
   *
   * @param  entry - The file to zip
   * @param  ctrl - The controller for the transform stream
   * @returns A promise that resolves when the file has been transformed
   */
  async transform(t, a) {
    const i = t.directory && !t.name.trim().endsWith("/") ? `${t.name.trim()}/` : t.name.trim();
    if (this.files[i]) {
      a.error(new Error("File already exists."));
      return;
    }
    const n = ke.encode(i);
    this.files[i] = {
      directory: !!t.directory,
      nameBuf: n,
      offset: this.offset,
      comment: ke.encode(t.comment ?? ""),
      compressedLength: g.BigInt(0),
      uncompressedLength: g.BigInt(0),
      header: new Uint8Array(26)
    };
    const o = this.files[i], { header: p } = o, r = new Date(t.lastModified ?? Date.now()), l = new DataView(p.buffer), s = new Uint8Array(30 + n.length);
    l.setUint32(0, 335546376), l.setUint16(6, (r.getHours() << 6 | r.getMinutes()) << 5 | r.getSeconds() / 2, !0), l.setUint16(8, (r.getFullYear() - 1980 << 4 | r.getMonth() + 1) << 5 | r.getDate(), !0), l.setUint16(22, n.length, !0), s.set([80, 75, 3, 4]), s.set(p, 4), s.set(n, 30), this.offset = g.add(this.offset, g.BigInt(s.length)), a.enqueue(s);
    const c = new Uint8Array(16);
    if (c.set([80, 75, 7, 8]), t.stream) {
      o.crc = new ge();
      const m = t.stream().getReader();
      for (; ; ) {
        const f = await m.read();
        if (f.done)
          break;
        const u = f.value;
        o.crc.append(u), o.uncompressedLength = g.add(o.uncompressedLength, g.BigInt(u.length)), o.compressedLength = g.add(o.compressedLength, g.BigInt(u.length)), a.enqueue(u);
      }
      l.setUint32(10, o.crc.get(), !0), l.setUint32(14, g.toNumber(o.compressedLength), !0), l.setUint32(18, g.toNumber(o.uncompressedLength), !0), c.set(p.subarray(10, 22), 4);
    }
    l.setUint16(22, n.length, !0), this.offset = g.add(this.offset, g.add(o.compressedLength, g.BigInt(16))), a.enqueue(c);
  }
  /**
   * @param  ctrl - The controller for the transform stream
   */
  flush(t) {
    let a = 0, i = 0, n;
    for (const r of Object.keys(this.files)) {
      if (n = this.files[r], !n)
        throw new TypeError(`File not found while flushing ZipTransformer: ${r}`);
      a += 46 + n.nameBuf.length + n.comment.length;
    }
    const o = new Uint8Array(a + 22), p = new DataView(o.buffer);
    for (const r of Object.keys(this.files)) {
      if (n = this.files[r], !n)
        throw new TypeError(`File not found while flushing ZipTransformer: ${r}`);
      p.setUint32(i, 1347092738), p.setUint16(i + 4, 5120), p.setUint16(i + 32, n.comment.length, !0), p.setUint8(i + 38, n.directory ? 16 : 0), p.setUint32(i + 42, g.toNumber(n.offset), !0), o.set(n.header, i + 6), o.set(n.nameBuf, i + 46), o.set(n.comment, i + 46 + n.nameBuf.length), i += 46 + n.nameBuf.length + n.comment.length;
    }
    p.setUint32(i, 1347093766), p.setUint16(i + 8, Object.keys(this.files).length, !0), p.setUint16(i + 10, Object.keys(this.files).length, !0), p.setUint32(i + 12, a, !0), p.setUint32(i + 16, g.toNumber(this.offset), !0), t.enqueue(o), this.files = /* @__PURE__ */ Object.create(null), this.offset = g.BigInt(0);
  }
}
class Rt extends TransformStream {
  constructor() {
    super(new It());
  }
}
const Ze = {
  "application/prs.cww": ["cww"],
  "application/prs.xsf+xml": ["xsf"],
  "application/vnd.1000minds.decision-model+xml": ["1km"],
  "application/vnd.3gpp.pic-bw-large": ["plb"],
  "application/vnd.3gpp.pic-bw-small": ["psb"],
  "application/vnd.3gpp.pic-bw-var": ["pvb"],
  "application/vnd.3gpp2.tcap": ["tcap"],
  "application/vnd.3m.post-it-notes": ["pwn"],
  "application/vnd.accpac.simply.aso": ["aso"],
  "application/vnd.accpac.simply.imp": ["imp"],
  "application/vnd.acucobol": ["acu"],
  "application/vnd.acucorp": ["atc", "acutc"],
  "application/vnd.adobe.air-application-installer-package+zip": ["air"],
  "application/vnd.adobe.formscentral.fcdt": ["fcdt"],
  "application/vnd.adobe.fxp": ["fxp", "fxpl"],
  "application/vnd.adobe.xdp+xml": ["xdp"],
  "application/vnd.adobe.xfdf": ["*xfdf"],
  "application/vnd.age": ["age"],
  "application/vnd.ahead.space": ["ahead"],
  "application/vnd.airzip.filesecure.azf": ["azf"],
  "application/vnd.airzip.filesecure.azs": ["azs"],
  "application/vnd.amazon.ebook": ["azw"],
  "application/vnd.americandynamics.acc": ["acc"],
  "application/vnd.amiga.ami": ["ami"],
  "application/vnd.android.package-archive": ["apk"],
  "application/vnd.anser-web-certificate-issue-initiation": ["cii"],
  "application/vnd.anser-web-funds-transfer-initiation": ["fti"],
  "application/vnd.antix.game-component": ["atx"],
  "application/vnd.apple.installer+xml": ["mpkg"],
  "application/vnd.apple.keynote": ["key"],
  "application/vnd.apple.mpegurl": ["m3u8"],
  "application/vnd.apple.numbers": ["numbers"],
  "application/vnd.apple.pages": ["pages"],
  "application/vnd.apple.pkpass": ["pkpass"],
  "application/vnd.aristanetworks.swi": ["swi"],
  "application/vnd.astraea-software.iota": ["iota"],
  "application/vnd.audiograph": ["aep"],
  "application/vnd.autodesk.fbx": ["fbx"],
  "application/vnd.balsamiq.bmml+xml": ["bmml"],
  "application/vnd.blueice.multipass": ["mpm"],
  "application/vnd.bmi": ["bmi"],
  "application/vnd.businessobjects": ["rep"],
  "application/vnd.chemdraw+xml": ["cdxml"],
  "application/vnd.chipnuts.karaoke-mmd": ["mmd"],
  "application/vnd.cinderella": ["cdy"],
  "application/vnd.citationstyles.style+xml": ["csl"],
  "application/vnd.claymore": ["cla"],
  "application/vnd.cloanto.rp9": ["rp9"],
  "application/vnd.clonk.c4group": ["c4g", "c4d", "c4f", "c4p", "c4u"],
  "application/vnd.cluetrust.cartomobile-config": ["c11amc"],
  "application/vnd.cluetrust.cartomobile-config-pkg": ["c11amz"],
  "application/vnd.commonspace": ["csp"],
  "application/vnd.contact.cmsg": ["cdbcmsg"],
  "application/vnd.cosmocaller": ["cmc"],
  "application/vnd.crick.clicker": ["clkx"],
  "application/vnd.crick.clicker.keyboard": ["clkk"],
  "application/vnd.crick.clicker.palette": ["clkp"],
  "application/vnd.crick.clicker.template": ["clkt"],
  "application/vnd.crick.clicker.wordbank": ["clkw"],
  "application/vnd.criticaltools.wbs+xml": ["wbs"],
  "application/vnd.ctc-posml": ["pml"],
  "application/vnd.cups-ppd": ["ppd"],
  "application/vnd.curl.car": ["car"],
  "application/vnd.curl.pcurl": ["pcurl"],
  "application/vnd.dart": ["dart"],
  "application/vnd.data-vision.rdz": ["rdz"],
  "application/vnd.dbf": ["dbf"],
  "application/vnd.dcmp+xml": ["dcmp"],
  "application/vnd.dece.data": ["uvf", "uvvf", "uvd", "uvvd"],
  "application/vnd.dece.ttml+xml": ["uvt", "uvvt"],
  "application/vnd.dece.unspecified": ["uvx", "uvvx"],
  "application/vnd.dece.zip": ["uvz", "uvvz"],
  "application/vnd.denovo.fcselayout-link": ["fe_launch"],
  "application/vnd.dna": ["dna"],
  "application/vnd.dolby.mlp": ["mlp"],
  "application/vnd.dpgraph": ["dpg"],
  "application/vnd.dreamfactory": ["dfac"],
  "application/vnd.ds-keypoint": ["kpxx"],
  "application/vnd.dvb.ait": ["ait"],
  "application/vnd.dvb.service": ["svc"],
  "application/vnd.dynageo": ["geo"],
  "application/vnd.ecowin.chart": ["mag"],
  "application/vnd.enliven": ["nml"],
  "application/vnd.epson.esf": ["esf"],
  "application/vnd.epson.msf": ["msf"],
  "application/vnd.epson.quickanime": ["qam"],
  "application/vnd.epson.salt": ["slt"],
  "application/vnd.epson.ssf": ["ssf"],
  "application/vnd.eszigno3+xml": ["es3", "et3"],
  "application/vnd.ezpix-album": ["ez2"],
  "application/vnd.ezpix-package": ["ez3"],
  "application/vnd.fdf": ["*fdf"],
  "application/vnd.fdsn.mseed": ["mseed"],
  "application/vnd.fdsn.seed": ["seed", "dataless"],
  "application/vnd.flographit": ["gph"],
  "application/vnd.fluxtime.clip": ["ftc"],
  "application/vnd.framemaker": ["fm", "frame", "maker", "book"],
  "application/vnd.frogans.fnc": ["fnc"],
  "application/vnd.frogans.ltf": ["ltf"],
  "application/vnd.fsc.weblaunch": ["fsc"],
  "application/vnd.fujitsu.oasys": ["oas"],
  "application/vnd.fujitsu.oasys2": ["oa2"],
  "application/vnd.fujitsu.oasys3": ["oa3"],
  "application/vnd.fujitsu.oasysgp": ["fg5"],
  "application/vnd.fujitsu.oasysprs": ["bh2"],
  "application/vnd.fujixerox.ddd": ["ddd"],
  "application/vnd.fujixerox.docuworks": ["xdw"],
  "application/vnd.fujixerox.docuworks.binder": ["xbd"],
  "application/vnd.fuzzysheet": ["fzs"],
  "application/vnd.genomatix.tuxedo": ["txd"],
  "application/vnd.geogebra.file": ["ggb"],
  "application/vnd.geogebra.slides": ["ggs"],
  "application/vnd.geogebra.tool": ["ggt"],
  "application/vnd.geometry-explorer": ["gex", "gre"],
  "application/vnd.geonext": ["gxt"],
  "application/vnd.geoplan": ["g2w"],
  "application/vnd.geospace": ["g3w"],
  "application/vnd.gmx": ["gmx"],
  "application/vnd.google-apps.document": ["gdoc"],
  "application/vnd.google-apps.drawing": ["gdraw"],
  "application/vnd.google-apps.form": ["gform"],
  "application/vnd.google-apps.jam": ["gjam"],
  "application/vnd.google-apps.map": ["gmap"],
  "application/vnd.google-apps.presentation": ["gslides"],
  "application/vnd.google-apps.script": ["gscript"],
  "application/vnd.google-apps.site": ["gsite"],
  "application/vnd.google-apps.spreadsheet": ["gsheet"],
  "application/vnd.google-earth.kml+xml": ["kml"],
  "application/vnd.google-earth.kmz": ["kmz"],
  "application/vnd.gov.sk.xmldatacontainer+xml": ["xdcf"],
  "application/vnd.grafeq": ["gqf", "gqs"],
  "application/vnd.groove-account": ["gac"],
  "application/vnd.groove-help": ["ghf"],
  "application/vnd.groove-identity-message": ["gim"],
  "application/vnd.groove-injector": ["grv"],
  "application/vnd.groove-tool-message": ["gtm"],
  "application/vnd.groove-tool-template": ["tpl"],
  "application/vnd.groove-vcard": ["vcg"],
  "application/vnd.hal+xml": ["hal"],
  "application/vnd.handheld-entertainment+xml": ["zmm"],
  "application/vnd.hbci": ["hbci"],
  "application/vnd.hhe.lesson-player": ["les"],
  "application/vnd.hp-hpgl": ["hpgl"],
  "application/vnd.hp-hpid": ["hpid"],
  "application/vnd.hp-hps": ["hps"],
  "application/vnd.hp-jlyt": ["jlt"],
  "application/vnd.hp-pcl": ["pcl"],
  "application/vnd.hp-pclxl": ["pclxl"],
  "application/vnd.hydrostatix.sof-data": ["sfd-hdstx"],
  "application/vnd.ibm.minipay": ["mpy"],
  "application/vnd.ibm.modcap": ["afp", "listafp", "list3820"],
  "application/vnd.ibm.rights-management": ["irm"],
  "application/vnd.ibm.secure-container": ["sc"],
  "application/vnd.iccprofile": ["icc", "icm"],
  "application/vnd.igloader": ["igl"],
  "application/vnd.immervision-ivp": ["ivp"],
  "application/vnd.immervision-ivu": ["ivu"],
  "application/vnd.insors.igm": ["igm"],
  "application/vnd.intercon.formnet": ["xpw", "xpx"],
  "application/vnd.intergeo": ["i2g"],
  "application/vnd.intu.qbo": ["qbo"],
  "application/vnd.intu.qfx": ["qfx"],
  "application/vnd.ipunplugged.rcprofile": ["rcprofile"],
  "application/vnd.irepository.package+xml": ["irp"],
  "application/vnd.is-xpr": ["xpr"],
  "application/vnd.isac.fcs": ["fcs"],
  "application/vnd.jam": ["jam"],
  "application/vnd.jcp.javame.midlet-rms": ["rms"],
  "application/vnd.jisp": ["jisp"],
  "application/vnd.joost.joda-archive": ["joda"],
  "application/vnd.kahootz": ["ktz", "ktr"],
  "application/vnd.kde.karbon": ["karbon"],
  "application/vnd.kde.kchart": ["chrt"],
  "application/vnd.kde.kformula": ["kfo"],
  "application/vnd.kde.kivio": ["flw"],
  "application/vnd.kde.kontour": ["kon"],
  "application/vnd.kde.kpresenter": ["kpr", "kpt"],
  "application/vnd.kde.kspread": ["ksp"],
  "application/vnd.kde.kword": ["kwd", "kwt"],
  "application/vnd.kenameaapp": ["htke"],
  "application/vnd.kidspiration": ["kia"],
  "application/vnd.kinar": ["kne", "knp"],
  "application/vnd.koan": ["skp", "skd", "skt", "skm"],
  "application/vnd.kodak-descriptor": ["sse"],
  "application/vnd.las.las+xml": ["lasxml"],
  "application/vnd.llamagraphics.life-balance.desktop": ["lbd"],
  "application/vnd.llamagraphics.life-balance.exchange+xml": ["lbe"],
  "application/vnd.lotus-1-2-3": ["123"],
  "application/vnd.lotus-approach": ["apr"],
  "application/vnd.lotus-freelance": ["pre"],
  "application/vnd.lotus-notes": ["nsf"],
  "application/vnd.lotus-organizer": ["org"],
  "application/vnd.lotus-screencam": ["scm"],
  "application/vnd.lotus-wordpro": ["lwp"],
  "application/vnd.macports.portpkg": ["portpkg"],
  "application/vnd.mapbox-vector-tile": ["mvt"],
  "application/vnd.mcd": ["mcd"],
  "application/vnd.medcalcdata": ["mc1"],
  "application/vnd.mediastation.cdkey": ["cdkey"],
  "application/vnd.mfer": ["mwf"],
  "application/vnd.mfmp": ["mfm"],
  "application/vnd.micrografx.flo": ["flo"],
  "application/vnd.micrografx.igx": ["igx"],
  "application/vnd.mif": ["mif"],
  "application/vnd.mobius.daf": ["daf"],
  "application/vnd.mobius.dis": ["dis"],
  "application/vnd.mobius.mbk": ["mbk"],
  "application/vnd.mobius.mqy": ["mqy"],
  "application/vnd.mobius.msl": ["msl"],
  "application/vnd.mobius.plc": ["plc"],
  "application/vnd.mobius.txf": ["txf"],
  "application/vnd.mophun.application": ["mpn"],
  "application/vnd.mophun.certificate": ["mpc"],
  "application/vnd.mozilla.xul+xml": ["xul"],
  "application/vnd.ms-artgalry": ["cil"],
  "application/vnd.ms-cab-compressed": ["cab"],
  "application/vnd.ms-excel": ["xls", "xlm", "xla", "xlc", "xlt", "xlw"],
  "application/vnd.ms-excel.addin.macroenabled.12": ["xlam"],
  "application/vnd.ms-excel.sheet.binary.macroenabled.12": ["xlsb"],
  "application/vnd.ms-excel.sheet.macroenabled.12": ["xlsm"],
  "application/vnd.ms-excel.template.macroenabled.12": ["xltm"],
  "application/vnd.ms-fontobject": ["eot"],
  "application/vnd.ms-htmlhelp": ["chm"],
  "application/vnd.ms-ims": ["ims"],
  "application/vnd.ms-lrm": ["lrm"],
  "application/vnd.ms-officetheme": ["thmx"],
  "application/vnd.ms-outlook": ["msg"],
  "application/vnd.ms-pki.seccat": ["cat"],
  "application/vnd.ms-pki.stl": ["*stl"],
  "application/vnd.ms-powerpoint": ["ppt", "pps", "pot"],
  "application/vnd.ms-powerpoint.addin.macroenabled.12": ["ppam"],
  "application/vnd.ms-powerpoint.presentation.macroenabled.12": ["pptm"],
  "application/vnd.ms-powerpoint.slide.macroenabled.12": ["sldm"],
  "application/vnd.ms-powerpoint.slideshow.macroenabled.12": ["ppsm"],
  "application/vnd.ms-powerpoint.template.macroenabled.12": ["potm"],
  "application/vnd.ms-project": ["*mpp", "mpt"],
  "application/vnd.ms-visio.viewer": ["vdx"],
  "application/vnd.ms-word.document.macroenabled.12": ["docm"],
  "application/vnd.ms-word.template.macroenabled.12": ["dotm"],
  "application/vnd.ms-works": ["wps", "wks", "wcm", "wdb"],
  "application/vnd.ms-wpl": ["wpl"],
  "application/vnd.ms-xpsdocument": ["xps"],
  "application/vnd.mseq": ["mseq"],
  "application/vnd.musician": ["mus"],
  "application/vnd.muvee.style": ["msty"],
  "application/vnd.mynfc": ["taglet"],
  "application/vnd.nato.bindingdataobject+xml": ["bdo"],
  "application/vnd.neurolanguage.nlu": ["nlu"],
  "application/vnd.nitf": ["ntf", "nitf"],
  "application/vnd.noblenet-directory": ["nnd"],
  "application/vnd.noblenet-sealer": ["nns"],
  "application/vnd.noblenet-web": ["nnw"],
  "application/vnd.nokia.n-gage.ac+xml": ["*ac"],
  "application/vnd.nokia.n-gage.data": ["ngdat"],
  "application/vnd.nokia.n-gage.symbian.install": ["n-gage"],
  "application/vnd.nokia.radio-preset": ["rpst"],
  "application/vnd.nokia.radio-presets": ["rpss"],
  "application/vnd.novadigm.edm": ["edm"],
  "application/vnd.novadigm.edx": ["edx"],
  "application/vnd.novadigm.ext": ["ext"],
  "application/vnd.oasis.opendocument.chart": ["odc"],
  "application/vnd.oasis.opendocument.chart-template": ["otc"],
  "application/vnd.oasis.opendocument.database": ["odb"],
  "application/vnd.oasis.opendocument.formula": ["odf"],
  "application/vnd.oasis.opendocument.formula-template": ["odft"],
  "application/vnd.oasis.opendocument.graphics": ["odg"],
  "application/vnd.oasis.opendocument.graphics-template": ["otg"],
  "application/vnd.oasis.opendocument.image": ["odi"],
  "application/vnd.oasis.opendocument.image-template": ["oti"],
  "application/vnd.oasis.opendocument.presentation": ["odp"],
  "application/vnd.oasis.opendocument.presentation-template": ["otp"],
  "application/vnd.oasis.opendocument.spreadsheet": ["ods"],
  "application/vnd.oasis.opendocument.spreadsheet-template": ["ots"],
  "application/vnd.oasis.opendocument.text": ["odt"],
  "application/vnd.oasis.opendocument.text-master": ["odm"],
  "application/vnd.oasis.opendocument.text-template": ["ott"],
  "application/vnd.oasis.opendocument.text-web": ["oth"],
  "application/vnd.olpc-sugar": ["xo"],
  "application/vnd.oma.dd2+xml": ["dd2"],
  "application/vnd.openblox.game+xml": ["obgx"],
  "application/vnd.openofficeorg.extension": ["oxt"],
  "application/vnd.openstreetmap.data+xml": ["osm"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    "pptx"
  ],
  "application/vnd.openxmlformats-officedocument.presentationml.slide": [
    "sldx"
  ],
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow": [
    "ppsx"
  ],
  "application/vnd.openxmlformats-officedocument.presentationml.template": [
    "potx"
  ],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [
    "xltx"
  ],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    "docx"
  ],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template": [
    "dotx"
  ],
  "application/vnd.osgeo.mapguide.package": ["mgp"],
  "application/vnd.osgi.dp": ["dp"],
  "application/vnd.osgi.subsystem": ["esa"],
  "application/vnd.palm": ["pdb", "pqa", "oprc"],
  "application/vnd.pawaafile": ["paw"],
  "application/vnd.pg.format": ["str"],
  "application/vnd.pg.osasli": ["ei6"],
  "application/vnd.picsel": ["efif"],
  "application/vnd.pmi.widget": ["wg"],
  "application/vnd.pocketlearn": ["plf"],
  "application/vnd.powerbuilder6": ["pbd"],
  "application/vnd.previewsystems.box": ["box"],
  "application/vnd.procrate.brushset": ["brushset"],
  "application/vnd.procreate.brush": ["brush"],
  "application/vnd.procreate.dream": ["drm"],
  "application/vnd.proteus.magazine": ["mgz"],
  "application/vnd.publishare-delta-tree": ["qps"],
  "application/vnd.pvi.ptid1": ["ptid"],
  "application/vnd.pwg-xhtml-print+xml": ["xhtm"],
  "application/vnd.quark.quarkxpress": [
    "qxd",
    "qxt",
    "qwd",
    "qwt",
    "qxl",
    "qxb"
  ],
  "application/vnd.rar": ["rar"],
  "application/vnd.realvnc.bed": ["bed"],
  "application/vnd.recordare.musicxml": ["mxl"],
  "application/vnd.recordare.musicxml+xml": ["musicxml"],
  "application/vnd.rig.cryptonote": ["cryptonote"],
  "application/vnd.rim.cod": ["cod"],
  "application/vnd.rn-realmedia": ["rm"],
  "application/vnd.rn-realmedia-vbr": ["rmvb"],
  "application/vnd.route66.link66+xml": ["link66"],
  "application/vnd.sailingtracker.track": ["st"],
  "application/vnd.seemail": ["see"],
  "application/vnd.sema": ["sema"],
  "application/vnd.semd": ["semd"],
  "application/vnd.semf": ["semf"],
  "application/vnd.shana.informed.formdata": ["ifm"],
  "application/vnd.shana.informed.formtemplate": ["itp"],
  "application/vnd.shana.informed.interchange": ["iif"],
  "application/vnd.shana.informed.package": ["ipk"],
  "application/vnd.simtech-mindmapper": ["twd", "twds"],
  "application/vnd.smaf": ["mmf"],
  "application/vnd.smart.teacher": ["teacher"],
  "application/vnd.software602.filler.form+xml": ["fo"],
  "application/vnd.solent.sdkm+xml": ["sdkm", "sdkd"],
  "application/vnd.spotfire.dxp": ["dxp"],
  "application/vnd.spotfire.sfs": ["sfs"],
  "application/vnd.stardivision.calc": ["sdc"],
  "application/vnd.stardivision.draw": ["sda"],
  "application/vnd.stardivision.impress": ["sdd"],
  "application/vnd.stardivision.math": ["smf"],
  "application/vnd.stardivision.writer": ["sdw", "vor"],
  "application/vnd.stardivision.writer-global": ["sgl"],
  "application/vnd.stepmania.package": ["smzip"],
  "application/vnd.stepmania.stepchart": ["sm"],
  "application/vnd.sun.wadl+xml": ["wadl"],
  "application/vnd.sun.xml.calc": ["sxc"],
  "application/vnd.sun.xml.calc.template": ["stc"],
  "application/vnd.sun.xml.draw": ["sxd"],
  "application/vnd.sun.xml.draw.template": ["std"],
  "application/vnd.sun.xml.impress": ["sxi"],
  "application/vnd.sun.xml.impress.template": ["sti"],
  "application/vnd.sun.xml.math": ["sxm"],
  "application/vnd.sun.xml.writer": ["sxw"],
  "application/vnd.sun.xml.writer.global": ["sxg"],
  "application/vnd.sun.xml.writer.template": ["stw"],
  "application/vnd.sus-calendar": ["sus", "susp"],
  "application/vnd.svd": ["svd"],
  "application/vnd.symbian.install": ["sis", "sisx"],
  "application/vnd.syncml+xml": ["xsm"],
  "application/vnd.syncml.dm+wbxml": ["bdm"],
  "application/vnd.syncml.dm+xml": ["xdm"],
  "application/vnd.syncml.dmddf+xml": ["ddf"],
  "application/vnd.tao.intent-module-archive": ["tao"],
  "application/vnd.tcpdump.pcap": ["pcap", "cap", "dmp"],
  "application/vnd.tmobile-livetv": ["tmo"],
  "application/vnd.trid.tpt": ["tpt"],
  "application/vnd.triscape.mxs": ["mxs"],
  "application/vnd.trueapp": ["tra"],
  "application/vnd.ufdl": ["ufd", "ufdl"],
  "application/vnd.uiq.theme": ["utz"],
  "application/vnd.umajin": ["umj"],
  "application/vnd.unity": ["unityweb"],
  "application/vnd.uoml+xml": ["uoml", "uo"],
  "application/vnd.vcx": ["vcx"],
  "application/vnd.visio": ["vsd", "vst", "vss", "vsw", "vsdx", "vtx"],
  "application/vnd.visionary": ["vis"],
  "application/vnd.vsf": ["vsf"],
  "application/vnd.wap.wbxml": ["wbxml"],
  "application/vnd.wap.wmlc": ["wmlc"],
  "application/vnd.wap.wmlscriptc": ["wmlsc"],
  "application/vnd.webturbo": ["wtb"],
  "application/vnd.wolfram.player": ["nbp"],
  "application/vnd.wordperfect": ["wpd"],
  "application/vnd.wqd": ["wqd"],
  "application/vnd.wt.stf": ["stf"],
  "application/vnd.xara": ["xar"],
  "application/vnd.xfdl": ["xfdl"],
  "application/vnd.yamaha.hv-dic": ["hvd"],
  "application/vnd.yamaha.hv-script": ["hvs"],
  "application/vnd.yamaha.hv-voice": ["hvp"],
  "application/vnd.yamaha.openscoreformat": ["osf"],
  "application/vnd.yamaha.openscoreformat.osfpvg+xml": ["osfpvg"],
  "application/vnd.yamaha.smaf-audio": ["saf"],
  "application/vnd.yamaha.smaf-phrase": ["spf"],
  "application/vnd.yellowriver-custom-menu": ["cmp"],
  "application/vnd.zul": ["zir", "zirz"],
  "application/vnd.zzazz.deck+xml": ["zaz"],
  "application/x-7z-compressed": ["7z"],
  "application/x-abiword": ["abw"],
  "application/x-ace-compressed": ["ace"],
  "application/x-apple-diskimage": ["*dmg"],
  "application/x-arj": ["arj"],
  "application/x-authorware-bin": ["aab", "x32", "u32", "vox"],
  "application/x-authorware-map": ["aam"],
  "application/x-authorware-seg": ["aas"],
  "application/x-bcpio": ["bcpio"],
  "application/x-bdoc": ["*bdoc"],
  "application/x-bittorrent": ["torrent"],
  "application/x-blender": ["blend"],
  "application/x-blorb": ["blb", "blorb"],
  "application/x-bzip": ["bz"],
  "application/x-bzip2": ["bz2", "boz"],
  "application/x-cbr": ["cbr", "cba", "cbt", "cbz", "cb7"],
  "application/x-cdlink": ["vcd"],
  "application/x-cfs-compressed": ["cfs"],
  "application/x-chat": ["chat"],
  "application/x-chess-pgn": ["pgn"],
  "application/x-chrome-extension": ["crx"],
  "application/x-cocoa": ["cco"],
  "application/x-compressed": ["*rar"],
  "application/x-conference": ["nsc"],
  "application/x-cpio": ["cpio"],
  "application/x-csh": ["csh"],
  "application/x-debian-package": ["*deb", "udeb"],
  "application/x-dgc-compressed": ["dgc"],
  "application/x-director": [
    "dir",
    "dcr",
    "dxr",
    "cst",
    "cct",
    "cxt",
    "w3d",
    "fgd",
    "swa"
  ],
  "application/x-doom": ["wad"],
  "application/x-dtbncx+xml": ["ncx"],
  "application/x-dtbook+xml": ["dtb"],
  "application/x-dtbresource+xml": ["res"],
  "application/x-dvi": ["dvi"],
  "application/x-envoy": ["evy"],
  "application/x-eva": ["eva"],
  "application/x-font-bdf": ["bdf"],
  "application/x-font-ghostscript": ["gsf"],
  "application/x-font-linux-psf": ["psf"],
  "application/x-font-pcf": ["pcf"],
  "application/x-font-snf": ["snf"],
  "application/x-font-type1": ["pfa", "pfb", "pfm", "afm"],
  "application/x-freearc": ["arc"],
  "application/x-futuresplash": ["spl"],
  "application/x-gca-compressed": ["gca"],
  "application/x-glulx": ["ulx"],
  "application/x-gnumeric": ["gnumeric"],
  "application/x-gramps-xml": ["gramps"],
  "application/x-gtar": ["gtar"],
  "application/x-hdf": ["hdf"],
  "application/x-httpd-php": ["php"],
  "application/x-install-instructions": ["install"],
  "application/x-ipynb+json": ["ipynb"],
  "application/x-iso9660-image": ["*iso"],
  "application/x-iwork-keynote-sffkey": ["*key"],
  "application/x-iwork-numbers-sffnumbers": ["*numbers"],
  "application/x-iwork-pages-sffpages": ["*pages"],
  "application/x-java-archive-diff": ["jardiff"],
  "application/x-java-jnlp-file": ["jnlp"],
  "application/x-keepass2": ["kdbx"],
  "application/x-latex": ["latex"],
  "application/x-lua-bytecode": ["luac"],
  "application/x-lzh-compressed": ["lzh", "lha"],
  "application/x-makeself": ["run"],
  "application/x-mie": ["mie"],
  "application/x-mobipocket-ebook": ["*prc", "mobi"],
  "application/x-ms-application": ["application"],
  "application/x-ms-shortcut": ["lnk"],
  "application/x-ms-wmd": ["wmd"],
  "application/x-ms-wmz": ["wmz"],
  "application/x-ms-xbap": ["xbap"],
  "application/x-msaccess": ["mdb"],
  "application/x-msbinder": ["obd"],
  "application/x-mscardfile": ["crd"],
  "application/x-msclip": ["clp"],
  "application/x-msdos-program": ["*exe"],
  "application/x-msdownload": ["*exe", "*dll", "com", "bat", "*msi"],
  "application/x-msmediaview": ["mvb", "m13", "m14"],
  "application/x-msmetafile": ["*wmf", "*wmz", "*emf", "emz"],
  "application/x-msmoney": ["mny"],
  "application/x-mspublisher": ["pub"],
  "application/x-msschedule": ["scd"],
  "application/x-msterminal": ["trm"],
  "application/x-mswrite": ["wri"],
  "application/x-netcdf": ["nc", "cdf"],
  "application/x-ns-proxy-autoconfig": ["pac"],
  "application/x-nzb": ["nzb"],
  "application/x-perl": ["pl", "pm"],
  "application/x-pilot": ["*prc", "*pdb"],
  "application/x-pkcs12": ["p12", "pfx"],
  "application/x-pkcs7-certificates": ["p7b", "spc"],
  "application/x-pkcs7-certreqresp": ["p7r"],
  "application/x-rar-compressed": ["*rar"],
  "application/x-redhat-package-manager": ["rpm"],
  "application/x-research-info-systems": ["ris"],
  "application/x-sea": ["sea"],
  "application/x-sh": ["sh"],
  "application/x-shar": ["shar"],
  "application/x-shockwave-flash": ["swf"],
  "application/x-silverlight-app": ["xap"],
  "application/x-sql": ["*sql"],
  "application/x-stuffit": ["sit"],
  "application/x-stuffitx": ["sitx"],
  "application/x-subrip": ["srt"],
  "application/x-sv4cpio": ["sv4cpio"],
  "application/x-sv4crc": ["sv4crc"],
  "application/x-t3vm-image": ["t3"],
  "application/x-tads": ["gam"],
  "application/x-tar": ["tar"],
  "application/x-tcl": ["tcl", "tk"],
  "application/x-tex": ["tex"],
  "application/x-tex-tfm": ["tfm"],
  "application/x-texinfo": ["texinfo", "texi"],
  "application/x-tgif": ["*obj"],
  "application/x-ustar": ["ustar"],
  "application/x-virtualbox-hdd": ["hdd"],
  "application/x-virtualbox-ova": ["ova"],
  "application/x-virtualbox-ovf": ["ovf"],
  "application/x-virtualbox-vbox": ["vbox"],
  "application/x-virtualbox-vbox-extpack": ["vbox-extpack"],
  "application/x-virtualbox-vdi": ["vdi"],
  "application/x-virtualbox-vhd": ["vhd"],
  "application/x-virtualbox-vmdk": ["vmdk"],
  "application/x-wais-source": ["src"],
  "application/x-web-app-manifest+json": ["webapp"],
  "application/x-x509-ca-cert": ["der", "crt", "pem"],
  "application/x-xfig": ["fig"],
  "application/x-xliff+xml": ["*xlf"],
  "application/x-xpinstall": ["xpi"],
  "application/x-xz": ["xz"],
  "application/x-zip-compressed": ["*zip"],
  "application/x-zmachine": ["z1", "z2", "z3", "z4", "z5", "z6", "z7", "z8"],
  "audio/vnd.dece.audio": ["uva", "uvva"],
  "audio/vnd.digital-winds": ["eol"],
  "audio/vnd.dra": ["dra"],
  "audio/vnd.dts": ["dts"],
  "audio/vnd.dts.hd": ["dtshd"],
  "audio/vnd.lucent.voice": ["lvp"],
  "audio/vnd.ms-playready.media.pya": ["pya"],
  "audio/vnd.nuera.ecelp4800": ["ecelp4800"],
  "audio/vnd.nuera.ecelp7470": ["ecelp7470"],
  "audio/vnd.nuera.ecelp9600": ["ecelp9600"],
  "audio/vnd.rip": ["rip"],
  "audio/x-aac": ["*aac"],
  "audio/x-aiff": ["aif", "aiff", "aifc"],
  "audio/x-caf": ["caf"],
  "audio/x-flac": ["flac"],
  "audio/x-m4a": ["*m4a"],
  "audio/x-matroska": ["mka"],
  "audio/x-mpegurl": ["m3u"],
  "audio/x-ms-wax": ["wax"],
  "audio/x-ms-wma": ["wma"],
  "audio/x-pn-realaudio": ["ram", "ra"],
  "audio/x-pn-realaudio-plugin": ["rmp"],
  "audio/x-realaudio": ["*ra"],
  "audio/x-wav": ["*wav"],
  "chemical/x-cdx": ["cdx"],
  "chemical/x-cif": ["cif"],
  "chemical/x-cmdf": ["cmdf"],
  "chemical/x-cml": ["cml"],
  "chemical/x-csml": ["csml"],
  "chemical/x-xyz": ["xyz"],
  "image/prs.btif": ["btif", "btf"],
  "image/prs.pti": ["pti"],
  "image/vnd.adobe.photoshop": ["psd"],
  "image/vnd.airzip.accelerator.azv": ["azv"],
  "image/vnd.dece.graphic": ["uvi", "uvvi", "uvg", "uvvg"],
  "image/vnd.djvu": ["djvu", "djv"],
  "image/vnd.dvb.subtitle": ["*sub"],
  "image/vnd.dwg": ["dwg"],
  "image/vnd.dxf": ["dxf"],
  "image/vnd.fastbidsheet": ["fbs"],
  "image/vnd.fpx": ["fpx"],
  "image/vnd.fst": ["fst"],
  "image/vnd.fujixerox.edmics-mmr": ["mmr"],
  "image/vnd.fujixerox.edmics-rlc": ["rlc"],
  "image/vnd.microsoft.icon": ["ico"],
  "image/vnd.ms-dds": ["dds"],
  "image/vnd.ms-modi": ["mdi"],
  "image/vnd.ms-photo": ["wdp"],
  "image/vnd.net-fpx": ["npx"],
  "image/vnd.pco.b16": ["b16"],
  "image/vnd.tencent.tap": ["tap"],
  "image/vnd.valve.source.texture": ["vtf"],
  "image/vnd.wap.wbmp": ["wbmp"],
  "image/vnd.xiff": ["xif"],
  "image/vnd.zbrush.pcx": ["pcx"],
  "image/x-3ds": ["3ds"],
  "image/x-adobe-dng": ["dng"],
  "image/x-cmu-raster": ["ras"],
  "image/x-cmx": ["cmx"],
  "image/x-freehand": ["fh", "fhc", "fh4", "fh5", "fh7"],
  "image/x-icon": ["*ico"],
  "image/x-jng": ["jng"],
  "image/x-mrsid-image": ["sid"],
  "image/x-ms-bmp": ["*bmp"],
  "image/x-pcx": ["*pcx"],
  "image/x-pict": ["pic", "pct"],
  "image/x-portable-anymap": ["pnm"],
  "image/x-portable-bitmap": ["pbm"],
  "image/x-portable-graymap": ["pgm"],
  "image/x-portable-pixmap": ["ppm"],
  "image/x-rgb": ["rgb"],
  "image/x-tga": ["tga"],
  "image/x-xbitmap": ["xbm"],
  "image/x-xpixmap": ["xpm"],
  "image/x-xwindowdump": ["xwd"],
  "message/vnd.wfa.wsc": ["wsc"],
  "model/vnd.bary": ["bary"],
  "model/vnd.cld": ["cld"],
  "model/vnd.collada+xml": ["dae"],
  "model/vnd.dwf": ["dwf"],
  "model/vnd.gdl": ["gdl"],
  "model/vnd.gtw": ["gtw"],
  "model/vnd.mts": ["*mts"],
  "model/vnd.opengex": ["ogex"],
  "model/vnd.parasolid.transmit.binary": ["x_b"],
  "model/vnd.parasolid.transmit.text": ["x_t"],
  "model/vnd.pytha.pyox": ["pyo", "pyox"],
  "model/vnd.sap.vds": ["vds"],
  "model/vnd.usda": ["usda"],
  "model/vnd.usdz+zip": ["usdz"],
  "model/vnd.valve.source.compiled-map": ["bsp"],
  "model/vnd.vtu": ["vtu"],
  "text/prs.lines.tag": ["dsc"],
  "text/vnd.curl": ["curl"],
  "text/vnd.curl.dcurl": ["dcurl"],
  "text/vnd.curl.mcurl": ["mcurl"],
  "text/vnd.curl.scurl": ["scurl"],
  "text/vnd.dvb.subtitle": ["sub"],
  "text/vnd.familysearch.gedcom": ["ged"],
  "text/vnd.fly": ["fly"],
  "text/vnd.fmi.flexstor": ["flx"],
  "text/vnd.graphviz": ["gv"],
  "text/vnd.in3d.3dml": ["3dml"],
  "text/vnd.in3d.spot": ["spot"],
  "text/vnd.sun.j2me.app-descriptor": ["jad"],
  "text/vnd.wap.wml": ["wml"],
  "text/vnd.wap.wmlscript": ["wmls"],
  "text/x-asm": ["s", "asm"],
  "text/x-c": ["c", "cc", "cxx", "cpp", "h", "hh", "dic"],
  "text/x-component": ["htc"],
  "text/x-fortran": ["f", "for", "f77", "f90"],
  "text/x-handlebars-template": ["hbs"],
  "text/x-java-source": ["java"],
  "text/x-lua": ["lua"],
  "text/x-markdown": ["mkd"],
  "text/x-nfo": ["nfo"],
  "text/x-opml": ["opml"],
  "text/x-org": ["*org"],
  "text/x-pascal": ["p", "pas"],
  "text/x-processing": ["pde"],
  "text/x-sass": ["sass"],
  "text/x-scss": ["scss"],
  "text/x-setext": ["etx"],
  "text/x-sfv": ["sfv"],
  "text/x-suse-ymp": ["ymp"],
  "text/x-uuencode": ["uu"],
  "text/x-vcalendar": ["vcs"],
  "text/x-vcard": ["vcf"],
  "video/vnd.dece.hd": ["uvh", "uvvh"],
  "video/vnd.dece.mobile": ["uvm", "uvvm"],
  "video/vnd.dece.pd": ["uvp", "uvvp"],
  "video/vnd.dece.sd": ["uvs", "uvvs"],
  "video/vnd.dece.video": ["uvv", "uvvv"],
  "video/vnd.dvb.file": ["dvb"],
  "video/vnd.fvt": ["fvt"],
  "video/vnd.mpegurl": ["mxu", "m4u"],
  "video/vnd.ms-playready.media.pyv": ["pyv"],
  "video/vnd.uvvu.mp4": ["uvu", "uvvu"],
  "video/vnd.vivo": ["viv"],
  "video/x-f4v": ["f4v"],
  "video/x-fli": ["fli"],
  "video/x-flv": ["flv"],
  "video/x-m4v": ["m4v"],
  "video/x-matroska": ["mkv", "mk3d", "mks"],
  "video/x-mng": ["mng"],
  "video/x-ms-asf": ["asf", "asx"],
  "video/x-ms-vob": ["vob"],
  "video/x-ms-wm": ["wm"],
  "video/x-ms-wmv": ["wmv"],
  "video/x-ms-wmx": ["wmx"],
  "video/x-ms-wvx": ["wvx"],
  "video/x-msvideo": ["avi"],
  "video/x-sgi-movie": ["movie"],
  "video/x-smv": ["smv"],
  "x-conference/x-cooltalk": ["ice"]
};
Object.freeze(Ze);
const Ve = {
  "application/andrew-inset": ["ez"],
  "application/appinstaller": ["appinstaller"],
  "application/applixware": ["aw"],
  "application/appx": ["appx"],
  "application/appxbundle": ["appxbundle"],
  "application/atom+xml": ["atom"],
  "application/atomcat+xml": ["atomcat"],
  "application/atomdeleted+xml": ["atomdeleted"],
  "application/atomsvc+xml": ["atomsvc"],
  "application/atsc-dwd+xml": ["dwd"],
  "application/atsc-held+xml": ["held"],
  "application/atsc-rsat+xml": ["rsat"],
  "application/automationml-aml+xml": ["aml"],
  "application/automationml-amlx+zip": ["amlx"],
  "application/bdoc": ["bdoc"],
  "application/calendar+xml": ["xcs"],
  "application/ccxml+xml": ["ccxml"],
  "application/cdfx+xml": ["cdfx"],
  "application/cdmi-capability": ["cdmia"],
  "application/cdmi-container": ["cdmic"],
  "application/cdmi-domain": ["cdmid"],
  "application/cdmi-object": ["cdmio"],
  "application/cdmi-queue": ["cdmiq"],
  "application/cpl+xml": ["cpl"],
  "application/cu-seeme": ["cu"],
  "application/cwl": ["cwl"],
  "application/dash+xml": ["mpd"],
  "application/dash-patch+xml": ["mpp"],
  "application/davmount+xml": ["davmount"],
  "application/dicom": ["dcm"],
  "application/docbook+xml": ["dbk"],
  "application/dssc+der": ["dssc"],
  "application/dssc+xml": ["xdssc"],
  "application/ecmascript": ["ecma"],
  "application/emma+xml": ["emma"],
  "application/emotionml+xml": ["emotionml"],
  "application/epub+zip": ["epub"],
  "application/exi": ["exi"],
  "application/express": ["exp"],
  "application/fdf": ["fdf"],
  "application/fdt+xml": ["fdt"],
  "application/font-tdpfr": ["pfr"],
  "application/geo+json": ["geojson"],
  "application/gml+xml": ["gml"],
  "application/gpx+xml": ["gpx"],
  "application/gxf": ["gxf"],
  "application/gzip": ["gz"],
  "application/hjson": ["hjson"],
  "application/hyperstudio": ["stk"],
  "application/inkml+xml": ["ink", "inkml"],
  "application/ipfix": ["ipfix"],
  "application/its+xml": ["its"],
  "application/java-archive": ["jar", "war", "ear"],
  "application/java-serialized-object": ["ser"],
  "application/java-vm": ["class"],
  "application/javascript": ["*js"],
  "application/json": ["json", "map"],
  "application/json5": ["json5"],
  "application/jsonml+json": ["jsonml"],
  "application/ld+json": ["jsonld"],
  "application/lgr+xml": ["lgr"],
  "application/lost+xml": ["lostxml"],
  "application/mac-binhex40": ["hqx"],
  "application/mac-compactpro": ["cpt"],
  "application/mads+xml": ["mads"],
  "application/manifest+json": ["webmanifest"],
  "application/marc": ["mrc"],
  "application/marcxml+xml": ["mrcx"],
  "application/mathematica": ["ma", "nb", "mb"],
  "application/mathml+xml": ["mathml"],
  "application/mbox": ["mbox"],
  "application/media-policy-dataset+xml": ["mpf"],
  "application/mediaservercontrol+xml": ["mscml"],
  "application/metalink+xml": ["metalink"],
  "application/metalink4+xml": ["meta4"],
  "application/mets+xml": ["mets"],
  "application/mmt-aei+xml": ["maei"],
  "application/mmt-usd+xml": ["musd"],
  "application/mods+xml": ["mods"],
  "application/mp21": ["m21", "mp21"],
  "application/mp4": ["*mp4", "*mpg4", "mp4s", "m4p"],
  "application/msix": ["msix"],
  "application/msixbundle": ["msixbundle"],
  "application/msword": ["doc", "dot"],
  "application/mxf": ["mxf"],
  "application/n-quads": ["nq"],
  "application/n-triples": ["nt"],
  "application/node": ["cjs"],
  "application/octet-stream": [
    "bin",
    "dms",
    "lrf",
    "mar",
    "so",
    "dist",
    "distz",
    "pkg",
    "bpk",
    "dump",
    "elc",
    "deploy",
    "exe",
    "dll",
    "deb",
    "dmg",
    "iso",
    "img",
    "msi",
    "msp",
    "msm",
    "buffer"
  ],
  "application/oda": ["oda"],
  "application/oebps-package+xml": ["opf"],
  "application/ogg": ["ogx"],
  "application/omdoc+xml": ["omdoc"],
  "application/onenote": [
    "onetoc",
    "onetoc2",
    "onetmp",
    "onepkg",
    "one",
    "onea"
  ],
  "application/oxps": ["oxps"],
  "application/p2p-overlay+xml": ["relo"],
  "application/patch-ops-error+xml": ["xer"],
  "application/pdf": ["pdf"],
  "application/pgp-encrypted": ["pgp"],
  "application/pgp-keys": ["asc"],
  "application/pgp-signature": ["sig", "*asc"],
  "application/pics-rules": ["prf"],
  "application/pkcs10": ["p10"],
  "application/pkcs7-mime": ["p7m", "p7c"],
  "application/pkcs7-signature": ["p7s"],
  "application/pkcs8": ["p8"],
  "application/pkix-attr-cert": ["ac"],
  "application/pkix-cert": ["cer"],
  "application/pkix-crl": ["crl"],
  "application/pkix-pkipath": ["pkipath"],
  "application/pkixcmp": ["pki"],
  "application/pls+xml": ["pls"],
  "application/postscript": ["ai", "eps", "ps"],
  "application/provenance+xml": ["provx"],
  "application/pskc+xml": ["pskcxml"],
  "application/raml+yaml": ["raml"],
  "application/rdf+xml": ["rdf", "owl"],
  "application/reginfo+xml": ["rif"],
  "application/relax-ng-compact-syntax": ["rnc"],
  "application/resource-lists+xml": ["rl"],
  "application/resource-lists-diff+xml": ["rld"],
  "application/rls-services+xml": ["rs"],
  "application/route-apd+xml": ["rapd"],
  "application/route-s-tsid+xml": ["sls"],
  "application/route-usd+xml": ["rusd"],
  "application/rpki-ghostbusters": ["gbr"],
  "application/rpki-manifest": ["mft"],
  "application/rpki-roa": ["roa"],
  "application/rsd+xml": ["rsd"],
  "application/rss+xml": ["rss"],
  "application/rtf": ["rtf"],
  "application/sbml+xml": ["sbml"],
  "application/scvp-cv-request": ["scq"],
  "application/scvp-cv-response": ["scs"],
  "application/scvp-vp-request": ["spq"],
  "application/scvp-vp-response": ["spp"],
  "application/sdp": ["sdp"],
  "application/senml+xml": ["senmlx"],
  "application/sensml+xml": ["sensmlx"],
  "application/set-payment-initiation": ["setpay"],
  "application/set-registration-initiation": ["setreg"],
  "application/shf+xml": ["shf"],
  "application/sieve": ["siv", "sieve"],
  "application/smil+xml": ["smi", "smil"],
  "application/sparql-query": ["rq"],
  "application/sparql-results+xml": ["srx"],
  "application/sql": ["sql"],
  "application/srgs": ["gram"],
  "application/srgs+xml": ["grxml"],
  "application/sru+xml": ["sru"],
  "application/ssdl+xml": ["ssdl"],
  "application/ssml+xml": ["ssml"],
  "application/swid+xml": ["swidtag"],
  "application/tei+xml": ["tei", "teicorpus"],
  "application/thraud+xml": ["tfi"],
  "application/timestamped-data": ["tsd"],
  "application/toml": ["toml"],
  "application/trig": ["trig"],
  "application/ttml+xml": ["ttml"],
  "application/ubjson": ["ubj"],
  "application/urc-ressheet+xml": ["rsheet"],
  "application/urc-targetdesc+xml": ["td"],
  "application/voicexml+xml": ["vxml"],
  "application/wasm": ["wasm"],
  "application/watcherinfo+xml": ["wif"],
  "application/widget": ["wgt"],
  "application/winhlp": ["hlp"],
  "application/wsdl+xml": ["wsdl"],
  "application/wspolicy+xml": ["wspolicy"],
  "application/xaml+xml": ["xaml"],
  "application/xcap-att+xml": ["xav"],
  "application/xcap-caps+xml": ["xca"],
  "application/xcap-diff+xml": ["xdf"],
  "application/xcap-el+xml": ["xel"],
  "application/xcap-ns+xml": ["xns"],
  "application/xenc+xml": ["xenc"],
  "application/xfdf": ["xfdf"],
  "application/xhtml+xml": ["xhtml", "xht"],
  "application/xliff+xml": ["xlf"],
  "application/xml": ["xml", "xsl", "xsd", "rng"],
  "application/xml-dtd": ["dtd"],
  "application/xop+xml": ["xop"],
  "application/xproc+xml": ["xpl"],
  "application/xslt+xml": ["*xsl", "xslt"],
  "application/xspf+xml": ["xspf"],
  "application/xv+xml": ["mxml", "xhvml", "xvml", "xvm"],
  "application/yang": ["yang"],
  "application/yin+xml": ["yin"],
  "application/zip": ["zip"],
  "application/zip+dotlottie": ["lottie"],
  "audio/3gpp": ["*3gpp"],
  "audio/aac": ["adts", "aac"],
  "audio/adpcm": ["adp"],
  "audio/amr": ["amr"],
  "audio/basic": ["au", "snd"],
  "audio/midi": ["mid", "midi", "kar", "rmi"],
  "audio/mobile-xmf": ["mxmf"],
  "audio/mp3": ["*mp3"],
  "audio/mp4": ["m4a", "mp4a", "m4b"],
  "audio/mpeg": ["mpga", "mp2", "mp2a", "mp3", "m2a", "m3a"],
  "audio/ogg": ["oga", "ogg", "spx", "opus"],
  "audio/s3m": ["s3m"],
  "audio/silk": ["sil"],
  "audio/wav": ["wav"],
  "audio/wave": ["*wav"],
  "audio/webm": ["weba"],
  "audio/xm": ["xm"],
  "font/collection": ["ttc"],
  "font/otf": ["otf"],
  "font/ttf": ["ttf"],
  "font/woff": ["woff"],
  "font/woff2": ["woff2"],
  "image/aces": ["exr"],
  "image/apng": ["apng"],
  "image/avci": ["avci"],
  "image/avcs": ["avcs"],
  "image/avif": ["avif"],
  "image/bmp": ["bmp", "dib"],
  "image/cgm": ["cgm"],
  "image/dicom-rle": ["drle"],
  "image/dpx": ["dpx"],
  "image/emf": ["emf"],
  "image/fits": ["fits"],
  "image/g3fax": ["g3"],
  "image/gif": ["gif"],
  "image/heic": ["heic"],
  "image/heic-sequence": ["heics"],
  "image/heif": ["heif"],
  "image/heif-sequence": ["heifs"],
  "image/hej2k": ["hej2"],
  "image/ief": ["ief"],
  "image/jaii": ["jaii"],
  "image/jais": ["jais"],
  "image/jls": ["jls"],
  "image/jp2": ["jp2", "jpg2"],
  "image/jpeg": ["jpg", "jpeg", "jpe"],
  "image/jph": ["jph"],
  "image/jphc": ["jhc"],
  "image/jpm": ["jpm", "jpgm"],
  "image/jpx": ["jpx", "jpf"],
  "image/jxl": ["jxl"],
  "image/jxr": ["jxr"],
  "image/jxra": ["jxra"],
  "image/jxrs": ["jxrs"],
  "image/jxs": ["jxs"],
  "image/jxsc": ["jxsc"],
  "image/jxsi": ["jxsi"],
  "image/jxss": ["jxss"],
  "image/ktx": ["ktx"],
  "image/ktx2": ["ktx2"],
  "image/pjpeg": ["jfif"],
  "image/png": ["png"],
  "image/sgi": ["sgi"],
  "image/svg+xml": ["svg", "svgz"],
  "image/t38": ["t38"],
  "image/tiff": ["tif", "tiff"],
  "image/tiff-fx": ["tfx"],
  "image/webp": ["webp"],
  "image/wmf": ["wmf"],
  "message/disposition-notification": ["disposition-notification"],
  "message/global": ["u8msg"],
  "message/global-delivery-status": ["u8dsn"],
  "message/global-disposition-notification": ["u8mdn"],
  "message/global-headers": ["u8hdr"],
  "message/rfc822": ["eml", "mime", "mht", "mhtml"],
  "model/3mf": ["3mf"],
  "model/gltf+json": ["gltf"],
  "model/gltf-binary": ["glb"],
  "model/iges": ["igs", "iges"],
  "model/jt": ["jt"],
  "model/mesh": ["msh", "mesh", "silo"],
  "model/mtl": ["mtl"],
  "model/obj": ["obj"],
  "model/prc": ["prc"],
  "model/step": ["step", "stp", "stpnc", "p21", "210"],
  "model/step+xml": ["stpx"],
  "model/step+zip": ["stpz"],
  "model/step-xml+zip": ["stpxz"],
  "model/stl": ["stl"],
  "model/u3d": ["u3d"],
  "model/vrml": ["wrl", "vrml"],
  "model/x3d+binary": ["*x3db", "x3dbz"],
  "model/x3d+fastinfoset": ["x3db"],
  "model/x3d+vrml": ["*x3dv", "x3dvz"],
  "model/x3d+xml": ["x3d", "x3dz"],
  "model/x3d-vrml": ["x3dv"],
  "text/cache-manifest": ["appcache", "manifest"],
  "text/calendar": ["ics", "ifb"],
  "text/coffeescript": ["coffee", "litcoffee"],
  "text/css": ["css"],
  "text/csv": ["csv"],
  "text/html": ["html", "htm", "shtml"],
  "text/jade": ["jade"],
  "text/javascript": ["js", "mjs"],
  "text/jsx": ["jsx"],
  "text/less": ["less"],
  "text/markdown": ["md", "markdown"],
  "text/mathml": ["mml"],
  "text/mdx": ["mdx"],
  "text/n3": ["n3"],
  "text/plain": ["txt", "text", "conf", "def", "list", "log", "in", "ini"],
  "text/richtext": ["rtx"],
  "text/rtf": ["*rtf"],
  "text/sgml": ["sgml", "sgm"],
  "text/shex": ["shex"],
  "text/slim": ["slim", "slm"],
  "text/spdx": ["spdx"],
  "text/stylus": ["stylus", "styl"],
  "text/tab-separated-values": ["tsv"],
  "text/troff": ["t", "tr", "roff", "man", "me", "ms"],
  "text/turtle": ["ttl"],
  "text/uri-list": ["uri", "uris", "urls"],
  "text/vcard": ["vcard"],
  "text/vtt": ["vtt"],
  "text/wgsl": ["wgsl"],
  "text/xml": ["*xml"],
  "text/yaml": ["yaml", "yml"],
  "video/3gpp": ["3gp", "3gpp"],
  "video/3gpp2": ["3g2"],
  "video/h261": ["h261"],
  "video/h263": ["h263"],
  "video/h264": ["h264"],
  "video/iso.segment": ["m4s"],
  "video/jpeg": ["jpgv"],
  "video/jpm": ["*jpm", "*jpgm"],
  "video/mj2": ["mj2", "mjp2"],
  "video/mp2t": ["ts", "m2t", "m2ts", "mts"],
  "video/mp4": ["mp4", "mp4v", "mpg4"],
  "video/mpeg": ["mpeg", "mpg", "mpe", "m1v", "m2v"],
  "video/ogg": ["ogv"],
  "video/quicktime": ["qt", "mov"],
  "video/webm": ["webm"]
};
Object.freeze(Ve);
var b = function(e, t, a, i) {
  if (a === "a" && !i) throw new TypeError("Private accessor was defined without a getter");
  if (typeof t == "function" ? e !== t || !i : !t.has(e)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
  return a === "m" ? i : a === "a" ? i.call(e) : i ? i.value : t.get(e);
}, I, N, _;
class At {
  constructor(...t) {
    I.set(this, /* @__PURE__ */ new Map()), N.set(this, /* @__PURE__ */ new Map()), _.set(this, /* @__PURE__ */ new Map());
    for (const a of t)
      this.define(a);
  }
  define(t, a = !1) {
    for (let [i, n] of Object.entries(t)) {
      i = i.toLowerCase(), n = n.map((r) => r.toLowerCase()), b(this, _, "f").has(i) || b(this, _, "f").set(i, /* @__PURE__ */ new Set());
      const o = b(this, _, "f").get(i);
      let p = !0;
      for (let r of n) {
        const l = r.startsWith("*");
        if (r = l ? r.slice(1) : r, o?.add(r), p && b(this, N, "f").set(i, r), p = !1, l)
          continue;
        const s = b(this, I, "f").get(r);
        if (s && s != i && !a)
          throw new Error(`"${i} -> ${r}" conflicts with "${s} -> ${r}". Pass \`force=true\` to override this definition.`);
        b(this, I, "f").set(r, i);
      }
    }
    return this;
  }
  getType(t) {
    if (typeof t != "string")
      return null;
    const a = t.replace(/^.*[/\\]/s, "").toLowerCase(), i = a.replace(/^.*\./s, "").toLowerCase(), n = a.length < t.length;
    return !(i.length < a.length - 1) && n ? null : b(this, I, "f").get(i) ?? null;
  }
  getExtension(t) {
    return typeof t != "string" ? null : (t = t?.split?.(";")[0], (t && b(this, N, "f").get(t.trim().toLowerCase())) ?? null);
  }
  getAllExtensions(t) {
    return typeof t != "string" ? null : b(this, _, "f").get(t.toLowerCase()) ?? null;
  }
  _freeze() {
    this.define = () => {
      throw new Error("define() not allowed for built-in Mime objects. See https://github.com/broofa/mime/blob/main/README.md#custom-mime-instances");
    }, Object.freeze(this);
    for (const t of b(this, _, "f").values())
      Object.freeze(t);
    return this;
  }
  _getTestState() {
    return {
      types: b(this, I, "f"),
      extensions: b(this, N, "f")
    };
  }
}
I = /* @__PURE__ */ new WeakMap(), N = /* @__PURE__ */ new WeakMap(), _ = /* @__PURE__ */ new WeakMap();
const Je = new At(Ve, Ze)._freeze();
/*! streamsaver. MIT License. Jimmy Wärting <https://jimmy.warting.se/opensource> */
const k = typeof window == "object" ? window : void 0;
k.HTMLElement || console.warn("streamsaver is meant to run on browsers main thread");
let w = null, le = !1;
const Ct = (e) => {
  try {
    e();
  } catch {
  }
}, Ut = k.WebStreamsPolyfill || {}, xe = k.isSecureContext;
let R = /constructor/i.test(k.HTMLElement) || !!k.safari || !!k.WebKitPoint;
const ae = xe || "MozAppearance" in document.documentElement.style ? "iframe" : "navigate", z = {
  createWriteStream: qt,
  WritableStream: k.WritableStream || Ut.WritableStream,
  supported: !0,
  version: { full: "2.0.5", major: 2, minor: 0, dot: 5 },
  mitm: "https://jimmywarting.github.io/StreamSaver.js/mitm.html?version=2.0.0"
};
function ce(e) {
  if (!e) throw new Error("meh");
  const t = document.createElement("iframe");
  return t.hidden = !0, t.src = e, t.loaded = !1, t.name = "iframe", t.isIframe = !0, t.postMessage = (...a) => t.contentWindow.postMessage(...a), t.addEventListener("load", () => {
    t.loaded = !0;
  }, { once: !0 }), document.body.appendChild(t), t;
}
function $t(e) {
  const t = "width=200,height=100", a = document.createDocumentFragment(), i = {
    frame: k.open(e, "popup", t),
    loaded: !1,
    isIframe: !1,
    isPopup: !0,
    remove() {
      i.frame.close();
    },
    addEventListener(...o) {
      a.addEventListener(...o);
    },
    dispatchEvent(...o) {
      a.dispatchEvent(...o);
    },
    removeEventListener(...o) {
      a.removeEventListener(...o);
    },
    postMessage(...o) {
      i.frame.postMessage(...o);
    }
  }, n = (o) => {
    o.source === i.frame && (i.loaded = !0, k.removeEventListener("message", n), i.dispatchEvent(new Event("load")));
  };
  return k.addEventListener("message", n), i;
}
try {
  new Response(new ReadableStream()), xe && !("serviceWorker" in navigator) && (R = !0);
} catch {
  R = !0;
}
Ct(() => {
  const { readable: e } = new TransformStream(), t = new MessageChannel();
  t.port1.postMessage(e, [e]), t.port1.close(), t.port2.close(), le = !0, Object.defineProperty(z, "TransformStream", {
    configurable: !1,
    writable: !1,
    value: TransformStream
  });
});
function Dt() {
  w || (w = xe ? ce(z.mitm) : $t(z.mitm));
}
function qt(e, t, a) {
  let i = {
    size: null,
    pathname: null,
    writableStrategy: void 0,
    readableStrategy: void 0
  }, n = 0, o = null, p = null, r = null;
  if (Number.isFinite(t) ? ([a, t] = [t, a], console.warn("[StreamSaver] Deprecated pass an object as 2nd argument when creating a write stream"), i.size = a, i.writableStrategy = t) : t && t.highWaterMark ? (console.warn("[StreamSaver] Deprecated pass an object as 2nd argument when creating a write stream"), i.size = a, i.writableStrategy = t) : i = t || {}, !R) {
    Dt(), p = new MessageChannel(), e = encodeURIComponent(e.replace(/\//g, ":")).replace(/['()]/g, escape).replace(/\*/g, "%2A");
    const s = {
      transferringReadable: le,
      pathname: i.pathname || Math.random().toString().slice(-6) + "/" + e,
      headers: {
        "Content-Type": "application/octet-stream; charset=utf-8",
        "Content-Disposition": "attachment; filename*=UTF-8''" + e
      }
    };
    i.size && (s.headers["Content-Length"] = i.size);
    const c = [s, "*", [p.port2]];
    if (le) {
      const m = ae === "iframe" ? void 0 : {
        // This transformer & flush method is only used by insecure context.
        transform(u, E) {
          if (!(u instanceof Uint8Array))
            throw new TypeError("Can only write Uint8Arrays");
          n += u.length, E.enqueue(u), o && (location.href = o, o = null);
        },
        flush() {
          o && (location.href = o);
        }
      };
      r = new z.TransformStream(
        m,
        i.writableStrategy,
        i.readableStrategy
      );
      const f = r.readable;
      p.port1.postMessage({ readableStream: f }, [f]);
    }
    p.port1.onmessage = (m) => {
      m.data.download ? ae === "navigate" ? (w.remove(), w = null, n ? location.href = m.data.download : o = m.data.download) : (w.isPopup && (w.remove(), w = null, ae === "iframe" && ce(z.mitm)), ce(m.data.download)) : m.data.abort && (l = [], p.port1.postMessage("abort"), p.port1.onmessage = null, p.port1.close(), p.port2.close(), p = null);
    }, w.loaded ? w.postMessage(...c) : w.addEventListener("load", () => {
      w.postMessage(...c);
    }, { once: !0 });
  }
  let l = [];
  return !R && r && r.writable || new z.WritableStream({
    write(s) {
      if (!(s instanceof Uint8Array))
        throw new TypeError("Can only write Uint8Arrays");
      if (R) {
        l.push(s);
        return;
      }
      p.port1.postMessage(s), n += s.length, o && (location.href = o, o = null);
    },
    close() {
      if (R) {
        const s = new Blob(l, { type: "application/octet-stream; charset=utf-8" }), c = document.createElement("a");
        c.href = URL.createObjectURL(s), c.download = e, c.click();
      } else
        p.port1.postMessage("end");
    },
    abort() {
      l = [], p.port1.postMessage("abort"), p.port1.onmessage = null, p.port1.close(), p.port2.close(), p = null;
    }
  }, i.writableStrategy);
}
class Qe {
  constructor(t) {
    t !== "streamsaver-default" && (z.mitm = t);
  }
  streamSaver = z;
}
new globalThis.TextDecoder("utf8");
function Wt(e) {
  if (typeof e != "string")
    throw new TypeError(`Expected \`string\`, got \`${typeof e}\``);
}
new globalThis.TextEncoder();
function Ot(e) {
  return e.replaceAll("-", "+").replaceAll("_", "/");
}
function Bt(e) {
  return Wt(e), Uint8Array.from(globalThis.atob(Ot(e)), (t) => t.codePointAt(0));
}
Array.from({ length: 256 }, (e, t) => t.toString(16).padStart(2, "0"));
function A(e) {
  return e instanceof Uint8Array ? e : Bt(e);
}
class Nt {
  isMainThread = !!self.document;
  /** Default log level is INFO */
  logLevel = 1;
  /** The thread that the logger is running in */
  thread = this.isMainThread ? "main" : "worker-null";
  /** Set the log level */
  setLogLevel(t) {
    this.logLevel = t;
  }
  /** Set the thread that the logger is running in */
  setThread(t) {
    this.thread = t;
  }
  /** Global tracker of worker job progress */
  workerJobTracker = this.isMainThread ? null : /* @__PURE__ */ new Map();
  /** Redact a RemoteResource for logging purposes */
  redactRemoteResource(t) {
    return t.decryptionOptions ? {
      ...t,
      decryptionOptions: {
        ...t.decryptionOptions,
        key: `[REDACTED] ${A(t.decryptionOptions.key).length.toString()}-byte key`
      }
    } : t;
  }
  debug(t, a) {
    this.logLevel <= 0 && console.debug(this.makeHeader(a), t);
  }
  info(t, a) {
    this.logLevel <= 1 && console.log(this.makeHeader(a), t);
  }
  warn(t, a) {
    this.logLevel <= 2 && console.warn(this.makeHeader(a), t);
  }
  error(t, a) {
    this.logLevel <= 3 && console.error(this.makeHeader(a), t);
  }
  /** The header for the logger */
  makeHeader(t) {
    return `[penumbra${t ? `:${t.split("-")[0] ?? ""}` : ""}:${this.thread}]`.padEnd(29, " ");
  }
}
const d = new Nt();
function Ee(e, t, a) {
  const n = {
    detail: {
      percent: a === null ? null : Math.round(t / a * 100),
      written: t,
      size: a
    }
  }, o = new S("progress", n);
  e.dispatchEvent(o);
}
function Ft(e) {
  const t = {
    detail: {}
  }, a = new S("complete", t);
  e.dispatchEvent(a);
}
const Ht = /^[\w-]+:\/{2,}\[?[\w.:-]+\]?(?::[0-9]*)?/;
function Zt(e) {
  const t = Ht.exec(e);
  if (!t)
    throw new Error("No origin found. Possibly invalid URL");
  return t[0];
}
function Vt(...e) {
  return [...new Set(e.map((t) => Zt(t)))];
}
const je = {
  /**
   * Get Blob cache (list of in-use object URLs)
   * @returns List of URLs
   */
  get() {
    const e = JSON.parse(localStorage.blobCache || "[]");
    if (!Array.isArray(e) || e.some((t) => !(t instanceof URL) && typeof t != "string"))
      throw new TypeError("Invalid blob cache");
    return e.map((t) => new URL(t));
  },
  /**
   * Write to Blob cache
   * @param cache - Cache
   */
  set(e) {
    localStorage.blobCache = JSON.stringify(e);
  },
  /** Clear Blob cache */
  clear() {
    for (const e of this.get())
      URL.revokeObjectURL(e);
    this.set([]);
  }
}, Jt = /^\s*(?:text\/[^/\s]+|application\/(?:xml|json)|[^/\s]+\/[^/\s]+\+(?:xml|json))\s*(?:$|;)/i;
function Qt(e) {
  return typeof e != "string" ? !1 : e.split("/")[0]?.trim().toLowerCase() === "text" || Jt.test(e);
}
const de = (e) => e !== null && !Number.isNaN(e), ie = [];
let ze = !1;
const ne = [];
let Se = !1;
const oe = [];
let _e = !1;
const pe = [];
let Te = !1;
const Y = {};
self.addEventListener("penumbra-progress", (e) => {
  const { handler: t } = Y;
  t ? (ze || (ie.map((a) => {
    t(a);
  }), ie.length = 0, ze = !0), t(e)) : ie.push(e);
});
self.addEventListener(
  "penumbra-complete",
  (e) => {
    const { handler: t } = Y;
    t ? (Se || (ne.map((a) => {
      t(a);
    }), ne.length = 0, Se = !0), t(e)) : ne.push(e);
  }
);
self.addEventListener("penumbra-error", (e) => {
  const { handler: t } = Y;
  t ? (_e || (oe.map((a) => {
    t(a);
  }), oe.length = 0, _e = !0), t(e)) : oe.push(e);
});
self.addEventListener("error", (e) => {
  const { handler: t } = Y;
  t ? (Te || (pe.map((a) => {
    t(a);
  }), pe.length = 0, Te = !0), t(e)) : pe.push(e);
});
const Pe = async (e) => {
  const t = await Promise.allSettled(e), a = t.filter(
    ({ status: i }) => i === "fulfilled"
  ).map(({ value: i }) => i).reduce((i, n) => i + n, 0);
  if (t.some(({ status: i }) => i === "rejected")) {
    const i = t.filter(
      ({ status: n }) => n === "rejected"
    );
    for (const n of i)
      d.error(n.reason, null);
    throw new AggregateError(
      i,
      `File${i.length > 1 ? "s" : ""} failed to be written to zip`
    );
  }
  return a;
};
class Gt extends EventTarget {
  /** Conflux zip writer instance */
  conflux = new Rt();
  /** Conflux WritableStream interface */
  writer = this.conflux.writable.getWriter();
  /** Save completion state */
  closed = !1;
  /** Save complete buffer */
  saveBuffer = !1;
  /** Promise representing completion of the zip stream piping to the file sink */
  pipePromise;
  /** Zip buffer used for testing */
  zipBufferPromise;
  /** Allow & auto-rename duplicate files sent to writer */
  allowDuplicates;
  /** All written & pending file paths */
  files = /* @__PURE__ */ new Set();
  /** Abort controller */
  controller;
  /** All pending finished and unfinished zip file writes */
  writes = [];
  /** Number of finished zip file writes */
  completedWrites = 0;
  /** Total zip archive size */
  byteSize = 0;
  /** Current zip archive size */
  bytesWritten = 0;
  /** Job ID */
  jobID;
  /**
   * Penumbra zip writer constructor
   * @param options - ZipOptions
   * @returns PenumbraZipWriter class instance
   */
  constructor({
    streamSaverEndpoint: t,
    name: a = "download",
    size: i,
    controller: n = new AbortController(),
    saveBuffer: o = !1,
    allowDuplicates: p = !0,
    onProgress: r,
    onComplete: l
  }, s) {
    super(), this.jobID = s, de(i) && (this.byteSize = i), this.allowDuplicates = p, this.controller = n;
    const { signal: c } = n;
    c.addEventListener(
      "abort",
      () => {
        this.close().catch((v) => {
          d.error(
            `Failed to close zip writer: ${v instanceof Error ? v.message : String(v)}`,
            null
          );
        });
      },
      { once: !0 }
    ), typeof r == "function" && this.addEventListener("progress", r), typeof l == "function" && this.addEventListener("complete", l, {
      once: !0
    });
    const { streamSaver: m } = new Qe(t), f = m.createWriteStream(
      // Append .zip to filename unless it is already present
      /\.zip\s*$/i.test(a) ? a : `${a}.zip`,
      { size: i }
    ), { readable: u } = this.conflux, [E, y] = o ? u.tee() : [u, null];
    this.pipePromise = E.pipeTo(f, { signal: c }), this.pipePromise.catch((v) => {
      const P = v instanceof Error ? v : typeof v == "string" ? new Error(v) : new Error("Unknown error"), M = new Error(
        `penumbra.saveZip() failed to create zip: ${P.message}`
      );
      d.error(M, null);
    }), o && y && (this.saveBuffer = o, this.zipBufferPromise = new Response(y).arrayBuffer(), this.zipBufferPromise.catch((v) => {
      const P = v instanceof Error ? v : typeof v == "string" ? new Error(v) : new Error("Unknown error"), M = new Error(
        `penumbra.saveZip() failed to buffer zip: ${P.message}`
      );
      d.error(M, null);
    }));
  }
  /**
   * Get observed zip size after all pending writes are resolved
   * @returns The size of zip
   */
  getSize() {
    return Pe(this.writes);
  }
  /**
   * Add decrypted PenumbraFiles to zip
   * @param files - Decrypted PenumbraFile[] to add to zip
   * @returns Total observed size of write call in bytes
   */
  write(...t) {
    const a = this, i = t.map(({ size: o }) => o), n = i.every((o) => de(o)) ? i.reduce((o, p) => o + p, 0) : null;
    return a.byteSize !== null && (n === null ? a.byteSize = null : a.byteSize += n), d.debug(
      `penumbra.saveZip(): writing ${t.length.toString()} files with total size: ${n?.toString() ?? "unknown"}. Files: ${JSON.stringify(
        t.map(
          (o) => d.redactRemoteResource(o)
        )
      )}`,
      this.jobID
    ), Pe(
      t.map(
        ({ path: o, filePrefix: p, stream: r, mimetype: l, lastModified: s = /* @__PURE__ */ new Date() }) => {
          let c = 0;
          const m = o ?? p;
          if (!m)
            throw new Error(
              "PenumbraZipWriter.write(): Unable to determine filename"
            );
          const [f, u] = m.split(/(\.\w+\s*$)/).filter(Boolean), E = l && Je.getExtension(l), y = u?.slice(1) ?? E ?? "";
          let v = `${f}.${y}`;
          if (a.files.has(v)) {
            const ee = v;
            let q = 0;
            for (; a.files.has(
              `${f} (${(++q).toString()}).${y}`
            ); ) ;
            v = `${f} (${q.toString()}).${y}`;
            const W = `penumbra.saveZip(): Duplicate file ${JSON.stringify(
              ee
            )}`;
            if (a.allowDuplicates)
              d.warn(
                `${W} renamed to ${JSON.stringify(v)}`,
                null
              );
            else
              throw a.abort(new Error(W)), new Error(W);
          }
          a.files.add(v);
          const P = r.getReader(), M = new Promise((ee, q) => {
            const W = new ReadableStream({
              /**
               * Start completion tracker-wrapped ReadableStream
               * @param controller - Controller
               */
              async start(h) {
                for (d.debug(
                  `penumbra.saveZip(): start zipping file: ${v}`,
                  a.jobID
                ); ; ) {
                  const { done: O, value: j } = await P.read();
                  if (j) {
                    const B = j.byteLength;
                    c += B, a.byteSize !== null && (a.bytesWritten += B, Ee(a, a.bytesWritten, a.byteSize));
                  }
                  if (O) {
                    if (ee(c), d.debug(
                      `penumbra.saveZip(): done zipping file: ${v} with size: ${c.toString()} bytes`,
                      a.jobID
                    ), a.completedWrites++, a.byteSize === null && Ee(
                      a,
                      a.completedWrites,
                      a.writes.length
                    ), a.completedWrites >= a.writes.length && Ft(a), n === null) {
                      let B = 0;
                      for (const rt of a.writes)
                        B += await rt;
                      a.byteSize ??= B;
                    }
                    break;
                  }
                  h.enqueue(j);
                }
                h.close(), P.releaseLock();
              },
              cancel: (h) => {
                const O = h instanceof Error ? h : typeof h == "string" ? new Error(h) : new Error("Unknown error"), j = new Error(
                  `penumbra.saveZip(): stream was cancelled: ${O.message}`
                );
                d.error(j, null), q(j);
              }
            });
            a.writer.write({
              name: v,
              lastModified: s,
              stream: () => W
            }).catch((h) => {
              const O = h instanceof Error ? h : typeof h == "string" ? new Error(h) : new Error("Unknown error"), j = new Error(
                `penumbra.saveZip(): failed to write file ${v}: ${O.message}`
              );
              d.error(j, null), q(j);
            });
          });
          return a.writes.push(M), M;
        }
      )
    );
  }
  /**
   * Enqueue closing of the Penumbra zip writer (after pending writes finish)
   * @returns Total observed zip size in bytes after close completes
   */
  async close() {
    d.debug("penumbra.saveZip(): close() called", this.jobID);
    const t = await this.getSize();
    if (!this.closed)
      try {
        await this.writer.close();
      } catch (a) {
        const i = a instanceof Error ? a : typeof a == "string" ? new Error(a) : new Error("Unknown error"), n = new Error(
          `penumbra.saveZip(): failed to close zip writer: ${i.message}`
        );
        throw d.error(n, null), n;
      }
    try {
      await this.pipePromise;
    } catch (a) {
      const i = a instanceof Error ? a : typeof a == "string" ? new Error(a) : new Error("Unknown error"), n = new Error(
        `penumbra.saveZip(): finished writing zip, but failed to download: ${i.message}`
      );
      throw d.error(n, null), n;
    }
    return t;
  }
  /** Cancel Penumbra zip writer */
  abort(t) {
    d.debug("penumbra.saveZip(): abort() called", this.jobID), this.controller.signal.aborted || this.controller.abort(t);
  }
  /**
   * Get buffered output (requires saveBuffer mode)
   * @returns buffer
   */
  getBuffer() {
    if (!this.closed)
      throw new Error(
        "getBuffer() can only be called when a PenumbraZipWriter is closed"
      );
    if (!this.saveBuffer || !this.zipBufferPromise)
      throw new Error(
        "getBuffer() can only be called on a PenumbraZipWriter in buffered mode, e.g. createZip({ saveBuffer: true })"
      );
    return this.zipBufferPromise;
  }
  /**
   * Get all written & pending file paths
   * @returns Files
   */
  getFiles() {
    return [...this.files];
  }
}
var F = /* @__PURE__ */ ((e) => (e[e.none = -0] = "none", e[e.full = 2] = "full", e))(F || {});
function Kt(e) {
  return e;
}
function Yt(e) {
  return e ? (t) => (e(t), t) : Kt;
}
const me = Yt((e) => {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(e))
    throw new TypeError(
      `Invalid JobID: ${e}. Must be a valid UUIDv4 string.`
    );
  return e;
});
function X() {
  return me(crypto.randomUUID());
}
const Ge = () => -0;
Ge.levels = F;
const _a = {
  get: () => Promise.resolve([]),
  save: () => Promise.resolve(),
  supported: Ge,
  preconnect: () => () => {
  },
  preload: () => () => {
  },
  encrypt: () => Promise.resolve({
    stream: new Response(new Blob()).body,
    size: 0,
    id: me("49e74657-f3b7-4777-994c-5f769a9828c5")
  }),
  decrypt: () => Promise.resolve({
    stream: new Response(new Blob()).body,
    id: me("55889332-22e6-420c-91e8-7cc2a6b9106e")
  }),
  getDecryptionInfo: () => Promise.resolve({
    key: new Uint8Array(32).fill(1),
    iv: new Uint8Array(12).fill(1),
    authTag: new Uint8Array(16).fill(1)
  }),
  getBlob: () => Promise.resolve(new Blob()),
  getTextOrURI: () => [
    Promise.resolve({ data: "test", type: "text", mimetype: "text/plain" })
  ],
  saveZip: () => ({
    /** Add PenumbraFiles to zip */
    write() {
    },
    /** Close zip writer */
    close() {
    },
    conflux: function() {
    },
    writer: {},
    controller: new AbortController(),
    aborted: !1
  }),
  setLogLevel: () => Promise.resolve()
};
function Xt(e) {
  return new ReadableStream(new ea(e));
}
class ea {
  constructor(t) {
    this._port = t, this._port.onmessage = (a) => this._onMessage(a.data);
  }
  start(t) {
    this._controller = t;
  }
  pull(t) {
    const a = {
      type: "pull"
    };
    this._port.postMessage(a);
  }
  cancel(t) {
    const a = {
      type: "error",
      reason: t
    };
    this._port.postMessage(a), this._port.close();
  }
  _onMessage(t) {
    switch (t.type) {
      case "write":
        this._controller.enqueue(t.chunk);
        break;
      case "abort":
        this._controller.error(t.reason), this._port.close();
        break;
      case "close":
        this._controller.close(), this._port.close();
        break;
    }
  }
}
function ta(e, t) {
  return new WritableStream(new aa(e, t));
}
class aa {
  constructor(t, a = {}) {
    this._port = t, this._transferChunk = a.transferChunk, this._resetReady(), this._port.onmessage = (i) => this._onMessage(i.data);
  }
  start(t) {
    return this._controller = t, this._readyPromise;
  }
  write(t, a) {
    const i = {
      type: "write",
      chunk: t
    };
    let n = this._transferChunk ? this._transferChunk(t) : [];
    return n.length ? this._port.postMessage(i, n) : this._port.postMessage(i), this._resetReady(), this._readyPromise;
  }
  close() {
    const t = {
      type: "close"
    };
    this._port.postMessage(t), this._port.close();
  }
  abort(t) {
    const a = {
      type: "abort",
      reason: t
    };
    this._port.postMessage(a), this._port.close();
  }
  _onMessage(t) {
    switch (t.type) {
      case "pull":
        this._resolveReady();
        break;
      case "error":
        this._onError(t.reason);
        break;
    }
  }
  _onError(t) {
    this._controller.error(t), this._rejectReady(t), this._port.close();
  }
  _resetReady() {
    this._readyPromise = new Promise((t, a) => {
      this._readyResolve = t, this._readyReject = a;
    }), this._readyPending = !0;
  }
  _resolveReady() {
    this._readyResolve(), this._readyPending = !1;
  }
  _rejectReady(t) {
    this._readyPending || this._resetReady(), this._readyPromise.catch(() => {
    }), this._readyReject(t), this._readyPending = !1;
  }
}
class he {
  constructor() {
    const t = new MessageChannel();
    this.writablePort = t.port1, this.readable = Xt(t.port2);
  }
}
class Ke {
  constructor(t) {
    const a = new MessageChannel();
    this.readablePort = a.port1, this.writable = ta(a.port2, t);
  }
}
const G = self;
if (!G.document)
  throw new Error(
    "Penumbra must be included in a document as an unbundled script element."
  );
let Ye = !1, fe = !1;
function ia(e) {
  G.dispatchEvent && G.dispatchEvent(e);
}
const { hardwareConcurrency: Me } = navigator, na = Me ? (
  // Reserve one thread (if hwConcurrency is supported) for UI renderer to prevent jank
  Me - 1
) : 4, x = [];
let Xe = 0;
async function oa() {
  const e = new Worker(new URL(
    /* @vite-ignore */
    "./assets/worker.penumbra-Bm-9K58b.js",
    import.meta.url
  ), {
    type: "module"
  }), t = Xe++, a = {
    worker: e,
    id: t,
    comlink: We(e),
    busy: !1
  }, i = a.comlink;
  return await (await new i()).setup(t, d.logLevel, Ne(ia)), a;
}
const ue = [];
async function be(e = 0) {
  const a = x.find(({ busy: i }) => !i) ?? x[Math.floor(Math.random() * x.length)];
  if (!a) {
    if (e > 3)
      throw d.warn("No workers available, giving up", null), new Error("Penumbra could not find a worker");
    return d.warn(
      `No workers available, retrying (attempt ${e.toString()})`,
      null
    ), await et(), be(e + 1);
  }
  return d.debug(`Freeing worker with ID: ${a.id.toString()}`, null), a.busy = !0, a;
}
function pa() {
  return new Promise((e) => {
    ue.push(() => {
      e(be());
    });
  });
}
const ra = Function.prototype.call.bind(Function.prototype.call);
async function et() {
  fe = !0, x.push(
    ...await Promise.all(
      // load all workers in parallel
      Array.from({ length: Math.max(na - x.length, 0) }).fill(0).map(() => oa())
    )
  ), fe = !1, Ye = !0;
  for (const e of ue)
    ra(e);
  d.debug(`Initialized ${x.length.toString()} workers`, null), ue.length = 0;
}
async function we() {
  return fe ? (d.debug("getWorker() waiting for initialization", null), pa()) : (Ye || (d.debug("getWorker() initializing workers", null), await et()), be());
}
function sa() {
  return x;
}
function la() {
  for (const e of sa())
    d.debug(
      `cleanup(): terminating workerID: ${e.id.toString()}`,
      null
    ), e.worker.terminate();
  x.length = 0, Xe = 0;
}
G.addEventListener("beforeunload", la);
const ca = ({
  detail: { worker: e }
}) => {
  if (typeof e == "number") {
    const t = x[e];
    if (!t) {
      d.debug(`Worker ${e.toString()} not found`, null);
      return;
    }
    t.busy = !1;
  }
};
addEventListener("penumbra-complete", ca);
async function da(e) {
  x.length > 0 && d.info(
    `penumbra.setLogLevel(): Syncing log level update to ${x.length.toString()} active workers. It's recommended to set the log level before calling penumbra's worker methods.`,
    null
  );
  const a = (await Promise.allSettled(
    x.map(async (i) => {
      const n = i.comlink;
      await (await new n()).updateLogLevel(e);
    })
  )).filter((i) => i.status === "rejected");
  if (a.length > 0)
    throw new Error(
      `Failed to update log level for ${a.length.toString()}/${x.length.toString()} workers:
 ${a.map((i) => i.reason instanceof Error ? i.reason.message : String(i.reason)).join(`
`)}`
    );
}
const ma = "WritableStream" in self && "TransformStream" in self;
let tt = F.none;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
typeof self.dispatchEvent == "function" && typeof self.CustomEvent == "function" && typeof self.Proxy == "function" && typeof self.Promise == "function" && typeof self.Response == "function" && typeof self.fetch == "function" && ma && (tt = F.full);
function at() {
  return tt;
}
at.levels = F;
function fa() {
  return () => {
  };
}
function it(e, t, a = !1) {
  if (self.document) {
    const i = e.map((n) => {
      const o = document.createElement("link");
      return o.rel = t, a && (o.as = "fetch", o.crossOrigin = "use-credentials"), o.href = n, document.head.append(o), o;
    });
    return () => i.map((n) => {
      n.remove();
    });
  }
  return fa();
}
function ua(...e) {
  const t = Vt(...e.map(({ url: a }) => a));
  return it(t, "preconnect");
}
function va(...e) {
  return it(
    e.map(({ url: t }) => t),
    "preload",
    !0
  );
}
const ve = 1024 * 1024 * 8, Le = ve * 16;
function nt() {
  return new TransformStream(
    {
      /**
       * Splits the data into chunks of 64 KiB.
       * @param chunk - The chunk of data to process.
       * @param controller - The controller to use to enqueue the processed data.
       */
      transform(e, t) {
        const a = e instanceof Uint8Array ? e : new Uint8Array(e);
        for (let i = 0; i < a.length; i += ve)
          t.enqueue(a.subarray(i, i + ve));
      }
    },
    new ByteLengthQueuingStrategy({ highWaterMark: Le }),
    new ByteLengthQueuingStrategy({ highWaterMark: Le })
  );
}
const Ie = 256, Re = 96, C = /* @__PURE__ */ new Map(), ga = ({
  detail: { id: e, decryptionInfo: t }
}) => {
  C.set(e, t);
};
self.addEventListener("penumbra-complete", ga);
const xa = (e) => new Promise((t) => {
  const a = setInterval(() => {
    if (C.has(e)) {
      const i = C.get(e);
      C.delete(e), clearInterval(a), t(i);
    }
  }, 100);
}), Ae = document.createElementNS(
  "http://www.w3.org/1999/xhtml",
  "a"
);
async function ha(e) {
  const t = new he(), { url: a } = e;
  Ae.href = a;
  const n = {
    path: Ae.pathname,
    ...e,
    stream: t.readable
  }, { writablePort: o } = t, p = X(), r = await we(), l = r.comlink;
  d.debug(
    `penumbra.get(): connecting to worker with workerID: ${r.id.toString()}`,
    p
  );
  const s = await new l();
  return d.debug(
    `penumbra.get(): requesting remote resource from worker: ${JSON.stringify(
      d.redactRemoteResource(e)
    )}`,
    p
  ), await s.get(U(o, [o]), e, p), { ...n, id: p };
}
function ba(...e) {
  if (e.length === 0)
    throw new Error("penumbra.get() called without arguments");
  return Promise.all(
    e.map((t, a) => {
      if (!("url" in t))
        throw new TypeError(
          `penumbra.get(): RemoteResource missing URL at index ${a.toString()}`
        );
      return ha(t);
    })
  );
}
const Ce = "download", wa = "application/octet-stream";
function ot(e) {
  const t = X();
  return d.debug(
    `penumbra.saveZip(): creating zip writer with options: ${JSON.stringify(
      e
    )}`,
    t
  ), new Gt(e, t);
}
async function ya(e, t, a, i = new AbortController()) {
  let n = 0;
  for (const u of e) {
    if (!de(u.size)) {
      n = void 0;
      break;
    }
    n += u.size;
  }
  if ("length" in e && e.length > 1) {
    const u = ot({
      name: a ?? `${Ce}.zip`,
      size: n,
      controller: i,
      streamSaverEndpoint: t
    });
    await u.write(...e), await u.close();
    return;
  }
  const o = "stream" in e ? e : e[0];
  if (!o)
    throw new Error("penumbra.save(): No file to save");
  const [p, r] = (a ?? o.filePrefix ?? Ce).split(/(\.\w+\s*$)/).filter(Boolean), l = o.mimetype && Je.getExtension(o.mimetype), s = r?.slice(1) ?? l ?? "", c = `${p}.${s}`, { signal: m } = i, { streamSaver: f } = new Qe(t);
  await o.stream.pipeTo(
    f.createWriteStream(c, {
      size: o.size
    }),
    { signal: m }
  );
}
function pt(e, t) {
  if ("length" in e && e.length > 1)
    throw new Error("penumbra.getBlob(): Called with multiple files");
  let a, i;
  if (e instanceof ReadableStream)
    a = e;
  else {
    const o = "length" in e ? e[0] : e;
    if (!o)
      throw new Error("penumbra.getBlob(): No file to get blob for");
    if (o.stream instanceof ArrayBuffer || ArrayBuffer.isView(o.stream))
      return Promise.resolve(
        new Blob(
          [
            new Uint8Array(
              o.stream,
              0,
              o.stream.byteLength
            )
          ],
          { ...o.mimetype ? { type: o.mimetype } : {} }
        )
      );
    a = o.stream, i = o.mimetype;
  }
  const n = new Headers({
    "Content-Type": t ?? i ?? wa
  });
  return new Response(a, { headers: n }).blob();
}
function ka(e) {
  const { id: t } = e;
  return C.has(t) ? Promise.resolve(C.get(t)) : xa(t);
}
async function Ea(e, t) {
  const a = X();
  let i = e?.key;
  i || (d.info(
    `penumbra.encrypt(): no key specified. generating a random ${Ie.toString()}-bit key`,
    a
  ), i = crypto.getRandomValues(
    new Uint8Array(Ie / 8)
  ));
  let n = e?.iv;
  n || (d.info(
    `penumbra.encrypt(): no IV specified. generating a random ${Re.toString()}-bit IV`,
    a
  ), n = crypto.getRandomValues(new Uint8Array(Re / 8)));
  const { writable: o, readablePort: p } = new Ke(), { readable: r, writablePort: l } = new he(), s = {
    // The `WritableStream` we write chunks to by reading from `files[i].stream`. Chunks are sent to the Worker.
    writable: o,
    // The `ReadableStream` we read chunks from and ultimately pass to the library consumer. Chunks are received from the Worker.
    readable: r
  }, c = await we();
  d.debug(
    `penumbra.encrypt(): connecting to worker with workerID: ${c.id.toString()}`,
    a
  );
  const m = c.comlink, f = await new m(), u = A(i), E = A(n);
  d.debug(
    "penumbra.encrypt(): requesting encryption stream from worker",
    a
  ), await f.encrypt(
    {
      key: u,
      iv: E,
      jobID: a,
      contentLength: t.size ?? null
    },
    U(p, [p]),
    U(l, [l])
  );
  const y = nt(), v = t.stream.pipeThrough(y).pipeThrough(s);
  return {
    ...t,
    id: a,
    stream: v
  };
}
async function ja(e, t) {
  if (!e.key || !e.iv || !e.authTag)
    throw new Error("penumbra.decrypt(): missing decryption options");
  const a = X(), { writable: i, readablePort: n } = new Ke(), { readable: o, writablePort: p } = new he(), r = {
    // The `WritableStream` we write chunks to by reading from `files[i].stream`. Chunks are sent to the Worker.
    writable: i,
    // The `ReadableStream` we read chunks from and ultimately pass to the library consumer. Chunks are received from the Worker.
    readable: o
  }, l = await we();
  d.debug(
    `penumbra.decrypt(): connecting to worker with workerID: ${l.id.toString()}`,
    a
  );
  const s = l.comlink, c = await new s(), m = A(e.key), f = A(e.iv), u = A(e.authTag);
  d.debug(
    "penumbra.decrypt(): requesting decryption stream from worker",
    a
  ), await c.decrypt(
    {
      key: m,
      iv: f,
      authTag: u,
      jobID: a,
      contentLength: t.size ?? null
    },
    U(n, [n]),
    U(p, [p])
  );
  const E = nt(), y = t.stream.pipeThrough(E).pipeThrough(r);
  return {
    ...t,
    id: a,
    stream: y
  };
}
function za(e) {
  return e.map(async (t) => {
    const { mimetype: a = "" } = t;
    if (a && Qt(a))
      return {
        type: "text",
        data: await new Response(t.stream).text(),
        mimetype: a
      };
    const i = URL.createObjectURL(await pt(t)), n = je.get();
    return n.push(new URL(i)), je.set(n), { type: "uri", data: i, mimetype: a };
  });
}
function Sa(e, t) {
  d.setLogLevel(e);
  const a = da(e).catch(
    (i) => {
      const n = `penumbra.setLogLevel(): Failed to sync log level update to all workers: ${i instanceof Error ? i.message : String(i)}`;
      if (t)
        throw new Error(n);
      d.error(n, null);
    }
  );
  if (t)
    return a;
}
const Ta = {
  preconnect: ua,
  preload: va,
  get: ba,
  encrypt: Ea,
  decrypt: ja,
  getDecryptionInfo: ka,
  save: ya,
  supported: at,
  getBlob: pt,
  getTextOrURI: za,
  saveZip: ot,
  setLogLevel: Sa
};
export {
  _a as MOCK_API,
  F as PenumbraSupportLevel,
  Gt as PenumbraZipWriter,
  Ta as penumbra
};
//# sourceMappingURL=main.penumbra.js.map
